import { useState, useRef, useEffect, useCallback, cloneElement } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../../context/AppContext";
import { Overlay } from "../../components/common/UI";
import DepartmentDropdown from "../../components/common/DepartmentSelect";
import DepartmentSelectionRequired from "../../components/common/DepartmentSelectionRequired";
import { useDepartments, useAutoSelectAssignedDepartment } from "../../hooks/useDepartments";
import {
  getLiveTourPreviewDepartmentId,
  notifyLiveTourTargetsChanged,
  useLiveTourDepartmentPrep,
  useLiveTourOptional,
} from "../../context/LiveTourContext";
import {
  afterValidationRender,
  fieldErrorBorder,
  scrollToFirstInvalidField,
} from "../../utils/formValidationFocus";
import FormScorePanel from "../../components/common/FormScorePanel";
import {
  Shield,
  Building2,
  Settings2,
  Scale,
  Users,
  Database,
  RefreshCw,
  Share2,
  Clock,
  Lock,
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Plus,
  Trash2,
  Menu,
  X,
  Check,
  MapPin,
  Mail,
  Phone,
  Tag,
  Target,
  Server,
  Key,
  Search,
  Bell,
  Globe,
  Calendar,
  FileText,
  Minus,
  Download,
  Award,
  LayoutList,
  ArrowRight,
  Bot,
  PenLine,
  ClipboardList,
} from "lucide-react";
import { exportSubmissionPdf } from "../../utils/exportSubmissionPdf";
import { showError } from "../../utils/sweetAlert";
import { ROPA_CONFIG } from "../../components/common/FormSubmissionsPage";

// ─── Section registry ─────────────────────────────────────────────────────────
const SECTIONS = [
  {
    id: "org",
    num: "01",
    title: "Organisation Details",
    short: "Org",
    Icon: Building2,
    accent: "#2563EB",
    accentLight: "#EFF6FF",
  },
  {
    id: "activity",
    num: "02",
    title: "Processing Activity",
    short: "Activity",
    Icon: Settings2,
    accent: "#7C3AED",
    accentLight: "#F5F3FF",
  },
  {
    id: "legal",
    num: "03",
    title: "Legal Basis",
    short: "Legal",
    Icon: Scale,
    accent: "#4338CA",
    accentLight: "#EEF2FF",
  },
  {
    id: "principals",
    num: "04",
    title: "Data Principals",
    short: "Principals",
    Icon: Users,
    accent: "#0D9488",
    accentLight: "#F0FDFA",
  },
  {
    id: "personaldata",
    num: "05",
    title: "Personal Data",
    short: "Data",
    Icon: Database,
    accent: "#0891B2",
    accentLight: "#ECFEFF",
  },
  {
    id: "lifecycle",
    num: "06",
    title: "Processing Lifecycle",
    short: "Lifecycle",
    Icon: RefreshCw,
    accent: "#D97706",
    accentLight: "#FFFBEB",
  },
  {
    id: "sharing",
    num: "07",
    title: "Data Sharing",
    short: "Sharing",
    Icon: Share2,
    accent: "#E11D48",
    accentLight: "#FFF1F2",
  },
  {
    id: "retention",
    num: "08",
    title: "Retention & Erasure",
    short: "Retention",
    Icon: Clock,
    accent: "#EA580C",
    accentLight: "#FFF7ED",
  },
  {
    id: "security",
    num: "09",
    title: "Security Safeguards",
    short: "Security",
    Icon: Lock,
    accent: "#334155",
    accentLight: "#F8FAFC",
  },
  {
    id: "rights",
    num: "10",
    title: "Rights Handling",
    short: "Rights",
    Icon: ShieldCheck,
    accent: "#16A34A",
    accentLight: "#F0FDF4",
  },
  {
    id: "incident",
    num: "11",
    title: "Incident Management",
    short: "Incidents",
    Icon: AlertTriangle,
    accent: "#DC2626",
    accentLight: "#FEF2F2",
  },
  {
    id: "approval",
    num: "12",
    title: "Approval & Sign-off",
    short: "Approval",
    Icon: CheckCircle2,
    accent: "#059669",
    accentLight: "#ECFDF5",
  },
];

const SECTION_DESCS = {
  org: "Record your organisation's identity, role under DPDP Act, and key contact details",
  activity:
    "Document the specific processing activity, its purpose, and the department responsible",
  legal:
    "Identify and confirm the lawful basis for each personal data processing activity",
  principals:
    "Define categories of data subjects whose personal data is collected or processed",
  personaldata:
    "Classify the types of personal data involved and their sensitivity levels",
  lifecycle:
    "Map the complete data lifecycle — collection source, storage, processing, and access",
  sharing:
    "Disclose all third-party recipients, sub-processors, and cross-border data transfers",
  retention:
    "Specify retention schedules, deletion triggers, and erasure methods per data category",
  security:
    "Document technical and organisational security safeguards in place",
  rights:
    "Define processes to fulfil Data Principal rights under the DPDP Act 2023",
  incident:
    "Document breach detection, notification procedures, and escalation paths",
  approval:
    "Capture sign-off by accountable persons and record the certification statement",
};

// ─── Data initialiser ─────────────────────────────────────────────────────────
function initData() {
  const d = {};
  SECTIONS.forEach((s) => {
    d[s.id] = ["sharing", "retention", "rights", "approval"].includes(s.id)
      ? { rows: [] }
      : {};
  });
  return d;
}

function detectLocked(draft) {
  if (!draft) return false;
  return Boolean(
    draft.is_locked ??
    draft.is_submitted ??
    draft.submitted ??
    (draft.status && String(draft.status).toLowerCase() === "submitted"),
  );
}

function mergeRopaData(draft) {
  const base = initData();
  if (!draft || typeof draft !== "object") return base;
  const merged = { ...base };
  SECTIONS.forEach((s) => {
    merged[s.id] = { ...base[s.id], ...(draft[s.id] || {}) };
  });
  return merged;
}

// ─── Section field schema (required for completion) ────────────────────────────
const SECTION_REQUIRED = {
  org: ["name", "role", "address", "email", "grievance", "sdf", "lastUpdated"],
  activity: ["name", "dept", "purpose", "desc"],
  legal: ["consent", "employment", "legalObligation", "publicInterest"],
  principals: ["customers", "employees", "applicants", "vendors", "minors"],
  personaldata: ["identity", "contact", "financial", "auth", "usageDevice"],
  lifecycle: ["source", "storage", "method", "access", "sharing"],
  security: ["encryption", "accessCtrl", "logging", "incident", "backup"],
  incident: ["identification", "dpbNotification", "timeline", "communication"],
};

const ROW_SECTIONS = {
  sharing: {
    columns: ["recipient", "role", "purpose", "location"],
    minRows: 1,
  },
  retention: {
    columns: ["category", "period", "trigger", "method"],
    minRows: 1,
  },
  rights: {
    columns: ["right", "process", "sla"],
    defaultRows: [
      { right: "Access", process: "", sla: "30" },
      { right: "Correction", process: "", sla: "30" },
      { right: "Erasure", process: "", sla: "30" },
      { right: "Consent Withdrawal", process: "", sla: "7" },
      { right: "Grievance Redressal", process: "", sla: "30" },
    ],
  },
  approval: {
    columns: ["role", "name", "signature", "date"],
    defaultRows: [
      { role: "Process Owner", name: "", signature: "", date: "" },
      { role: "Grievance Officer / DPO", name: "", signature: "", date: "" },
      { role: "Legal / Compliance Head", name: "", signature: "", date: "" },
    ],
  },
};

function isRopaScalarFilled(value) {
  return String(value ?? "").trim() !== "";
}

function isRopaRowComplete(row, columns) {
  return columns.every((col) => isRopaScalarFilled(row?.[col]));
}

function getEffectiveRows(sectionId, data) {
  const spec = ROW_SECTIONS[sectionId];
  if (!spec) return [];
  const rows = data[sectionId]?.rows;
  if (Array.isArray(rows) && rows.length) return rows;
  return spec.defaultRows || [];
}

function getRequiredScalarKeys(sectionId, schemaFields = []) {
  const sectionSchema = schemaFields.filter(
    (f) => f.section_id === sectionId && f.is_active !== false,
  );
  if (sectionSchema.length > 0) {
    return sectionSchema
      .filter(isRopaFieldRequired)
      .map((f) => f.external_id);
  }
  return SECTION_REQUIRED[sectionId] || [];
}

function getRequiredRowColumns(sectionId, schemaFields = [], defaultColumns = []) {
  const sectionSchema = schemaFields.filter(
    (f) => f.section_id === sectionId && f.is_active !== false,
  );
  if (sectionSchema.length > 0) {
    return defaultColumns.filter((col) => {
      const field = sectionSchema.find((f) => f.external_id === col);
      return field ? isRopaFieldRequired(field) : true;
    });
  }
  return defaultColumns;
}

function isRopaRowCompleteForColumns(row, columns) {
  return columns.every((col) => isRopaScalarFilled(row?.[col]));
}

function isSectionComplete(sectionId, data, schemaFields = []) {
  const rowSpec = ROW_SECTIONS[sectionId];
  if (rowSpec) {
    const rows = getEffectiveRows(sectionId, data);
    const minRows = rowSpec.minRows ?? rows.length;
    if (rows.length < minRows) return false;
    const requiredCols = getRequiredRowColumns(
      sectionId,
      schemaFields,
      rowSpec.columns,
    );
    if (!requiredCols.length) return true;
    return rows.every((row) => isRopaRowCompleteForColumns(row, requiredCols));
  }
  const keys = getRequiredScalarKeys(sectionId, schemaFields);
  if (!keys.length) return true;
  const d = data[sectionId] || {};
  return keys.every((key) => isRopaScalarFilled(d[key]));
}

function countSectionFilledFields(sectionId, data, schemaFields = []) {
  const rowSpec = ROW_SECTIONS[sectionId];
  if (rowSpec) {
    const rows = getEffectiveRows(sectionId, data);
    const requiredCols = getRequiredRowColumns(
      sectionId,
      schemaFields,
      rowSpec.columns,
    );
    let filled = 0;
    let total = 0;
    rows.forEach((row) => {
      requiredCols.forEach((col) => {
        total += 1;
        if (isRopaScalarFilled(row?.[col])) filled += 1;
      });
    });
    return { filled, total };
  }
  const keys = getRequiredScalarKeys(sectionId, schemaFields);
  const d = data[sectionId] || {};
  let filled = 0;
  keys.forEach((key) => {
    if (isRopaScalarFilled(d[key])) filled += 1;
  });
  return { filled, total: keys.length };
}

function getSectionFilled(sectionId, data, schemaFields = []) {
  const { filled } = countSectionFilledFields(sectionId, data, schemaFields);
  return filled;
}

function schemaField(schemaFields, sectionId, externalId) {
  if (!sectionId) {
    return schemaFields.find((f) => f.external_id === externalId) || null;
  }
  return (
    schemaFields.find(
      (f) => f.section_id === sectionId && f.external_id === externalId,
    ) || null
  );
}

function isRopaFieldRequired(field) {
  return field?.required === true;
}

function normalizeRopaSchemaField(f) {
  return {
    ...f,
    required: f.required === false ? false : true,
    order: Number(f.order) || 0,
  };
}

function normalizeRopaSchemaFields(fields) {
  return (fields || []).map(normalizeRopaSchemaField);
}

function orderSectionItems(items, sectionId, schemaFields) {
  const sectionFields = schemaFields.filter(
    (f) => f.section_id === sectionId && f.is_active !== false,
  );
  if (!sectionFields.length) return items;
  const orderedKeys = [...sectionFields]
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((f) => f.external_id);
  const itemMap = Object.fromEntries(items.map((it) => [it.key, it]));
  const result = [];
  orderedKeys.forEach((key) => {
    if (itemMap[key]) result.push(itemMap[key]);
  });
  items.forEach((it) => {
    if (!orderedKeys.includes(it.key)) result.push(it);
  });
  return result;
}

function renderRopaField(sectionId, id, element, schemaFields) {
  const field = schemaField(schemaFields, sectionId, id);
  if (!field) return element;
  const required = isRopaFieldRequired(field);
  const props = { required, optional: !required, sectionId };
  if (field.text) props.label = field.text;
  return cloneElement(element, props);
}

function schemaOpts(field) {
  return (field?.options || []).map((o) =>
    typeof o === "string" ? o : o.label || "",
  );
}

function AttachmentUpload({ fieldId, required }) {
  const [file, setFile] = useState(null);
  return (
    <div
      style={{
        marginTop: 8,
        padding: "10px 12px",
        borderRadius: 10,
        border: `1.5px dashed ${required ? "#f59e0b" : "rgba(99,102,241,0.25)"}`,
        background: required ? "#fffbeb" : "#f8fafc",
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: required ? "#92400e" : "#64748b",
          marginBottom: 6,
        }}
      >
        {required ? "Attachment Required *" : "Attachment (optional)"}
      </div>
      <input
        type="file"
        id={`attach-${fieldId}`}
        style={{ display: "none" }}
        onChange={(e) => setFile(e.target.files?.[0] || null)}
      />
      <label
        htmlFor={`attach-${fieldId}`}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "5px 12px",
          borderRadius: 8,
          cursor: "pointer",
          fontSize: 12,
          background: "#fff",
          border: "1px solid rgba(99,102,241,0.2)",
          color: "#4338ca",
          fontWeight: 500,
        }}
      >
        📎 {file ? file.name : "Choose file"}
      </label>
      {file && (
        <span style={{ marginLeft: 8, fontSize: 11, color: "#64748b" }}>
          {(file.size / 1024).toFixed(1)} KB
        </span>
      )}
    </div>
  );
}

function DynamicField({ field, d, set, errors, accent = "#6366f1" }) {
  const id = field.external_id;
  const sectionId = field.section_id;
  const label = field.text;
  const required = isRopaFieldRequired(field);
  const opts = schemaOpts(field);
  const val = d[id] ?? (field.field_type === "multiselect" ? [] : "");
  const error = errors[id];

  let fieldEl = null;

  switch (field.field_type) {
    case "toggle":
      fieldEl = (
        <ToggleRow
          label={label}
          note={field.description || field.config?.note || ""}
          accent={accent}
          fieldId={id}
          value={d[id] || ""}
          onChange={set(id)}
          noteValue={d[`${id}Note`] || ""}
          onNoteChange={set(`${id}Note`)}
          error={error}
        />
      );
      break;
    case "select":
    case "boolean":
      fieldEl = (
        <FSelect
          label={label}
          id={id}
          sectionId={sectionId}
          required={required}
          optional={!required}
          options={opts}
          value={val}
          onChange={set(id)}
          error={error}
        />
      );
      break;
    case "multiselect":
      fieldEl = (
        <FMultiSelect
          label={label}
          id={id}
          sectionId={sectionId}
          required={required}
          optional={!required}
          options={opts}
          value={Array.isArray(val) ? val : []}
          onChange={set(id)}
          error={error}
        />
      );
      break;
    case "textarea":
      fieldEl = (
        <FTextarea
          label={label}
          id={id}
          sectionId={sectionId}
          required={required}
          optional={!required}
          value={val}
          onChange={set(id)}
          error={error}
        />
      );
      break;
    case "date":
      fieldEl = (
        <FInput
          label={label}
          id={id}
          sectionId={sectionId}
          required={required}
          optional={!required}
          type="date"
          value={val}
          onChange={set(id)}
          error={error}
        />
      );
      break;
    default:
      fieldEl = (
        <FInput
          label={label}
          id={id}
          sectionId={sectionId}
          required={required}
          optional={!required}
          value={val}
          onChange={set(id)}
          error={error}
        />
      );
      break;
  }

  return (
    <div key={id} style={{ width: "100%" }}>
      {fieldEl}
      {field.allow_attachment && (
        <AttachmentUpload
          fieldId={id}
          required={isRopaFieldRequired(field) && field.attachment_required}
        />
      )}
    </div>
  );
}

function validateSection(sectionId, data, schemaFields = []) {
  const e = {};
  const rowSpec = ROW_SECTIONS[sectionId];
  if (rowSpec) {
    const rows = getEffectiveRows(sectionId, data);
    const minRows = rowSpec.minRows ?? rows.length;
    const requiredCols = getRequiredRowColumns(
      sectionId,
      schemaFields,
      rowSpec.columns,
    );
    if (minRows > 0 && rows.length < minRows) {
      e.rows = "Add at least one complete row";
      return e;
    }
    rows.forEach((row, ri) => {
      requiredCols.forEach((col) => {
        if (!isRopaScalarFilled(row?.[col])) {
          e[`rows-${ri}-${col}`] = "Required";
        }
      });
    });
    if (Object.keys(e).length && !e.rows)
      e.rows = "Complete all required cells in each row";
    return e;
  }
  const keys = getRequiredScalarKeys(sectionId, schemaFields);
  const d = data[sectionId] || {};
  keys.forEach((key) => {
    const field = schemaField(schemaFields, sectionId, key);
    if (field && !isRopaFieldRequired(field)) return;
    if (!isRopaScalarFilled(d[key])) e[key] = "This field is required";
  });
  return e;
}

function findFirstInvalidStep(data, schemaFields = []) {
  for (let i = 0; i < SECTIONS.length; i++) {
    const errs = validateSection(SECTIONS[i].id, data, schemaFields);
    if (Object.keys(errs).length) return { stepIndex: i, errors: errs };
  }
  return null;
}

// ─── Shared input styles ──────────────────────────────────────────────────────
const inputStyle = (focused = false) => ({
  width: "100%",
  fontSize: 12,
  padding: "9px 12px",
  borderRadius: 10,
  background: "#ffffff",
  border: `1.5px solid ${focused ? "#6366f1" : "rgba(99,102,241,0.18)"}`,
  color: "#0f172a",
  outline: "none",
  fontFamily: "inherit",
  boxShadow: focused
    ? "0 0 0 3px rgba(99,102,241,0.1)"
    : "0 1px 2px rgba(0,0,0,0.04)",
  transition: "border-color 0.15s, box-shadow 0.15s",
});

// ─── Field Label ──────────────────────────────────────────────────────────────
function FL({ children, required, optional, fieldId, sectionId, subsection }) {
  let displaySub = subsection || "";
  let displayId = fieldId;
  let showRequired = required;
  let showOptional = optional;

  if (fieldId && window.__ropaSchemaFields) {
    const f = sectionId
      ? window.__ropaSchemaFields.find(
          (x) => x.external_id === fieldId && x.section_id === sectionId,
        )
      : window.__ropaSchemaFields.find((x) => x.external_id === fieldId);
    if (f) {
      showRequired = isRopaFieldRequired(f);
      showOptional = !isRopaFieldRequired(f);
      // Prefer tenant-admin-edited description over any passed-in subsection
      displaySub =
        f.description || f.config?.dpdp_ref || subsection || "";
      // Also resolve clean generated display ID (e.g. ORG01) for hardcoded fields
      if (!/^[A-Z]{3,4}\d+$/.test(displayId)) {
        const codes = {
          org: "ORG",
          activity: "ACT",
          legal: "LEG",
          principals: "PRN",
          personaldata: "DAT",
          lifecycle: "LFC",
          sharing: "SHR",
          retention: "RET",
          security: "SEC",
          rights: "RGT",
          incident: "INC",
          approval: "APP",
        };
        const sectionItems = window.__ropaSchemaFields
          .filter(
            (item) =>
              item.section_id === f.section_id && item.is_active !== false,
          )
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        const idx = sectionItems.findIndex(
          (item) => item.external_id === fieldId,
        );
        const num = idx !== -1 ? idx + 1 : 1;
        const secCode = codes[f.section_id] || "GEN";
        displayId = `${secCode}${String(num).padStart(2, "0")}`;
      }
    }
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        marginBottom: 6,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 4,
        }}
      >
        <label
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: "#1e293b",
            textTransform: "uppercase",
            letterSpacing: "0.07em",
          }}
        >
          {children}
          {showRequired && (
            <span style={{ color: "#ef4444", marginLeft: 2 }}>*</span>
          )}
        </label>
        {showOptional && (
          <span style={{ fontSize: 10, color: "#64748b", fontWeight: 500 }}>
            Optional
          </span>
        )}
      </div>
      {(displayId || displaySub) && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginTop: 2,
            marginBottom: 4,
            flexWrap: "wrap",
          }}
        >
          {displayId && (
            <span
              style={{
                fontSize: 9,
                fontFamily: "monospace",
                fontWeight: 600,
                padding: "2px 6px",
                borderRadius: 5,
                background: "#f1f5f9",
                color: "#1e293b",
                border: "1px solid rgba(99,102,241,0.1)",
              }}
            >
              {displayId}
            </span>
          )}
          {displaySub && (
            <span style={{ fontSize: 10, color: "#1e293b" }}>{displaySub}</span>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Primitive fields ─────────────────────────────────────────────────────────
function FInput({
  label,
  placeholder,
  required,
  optional,
  value,
  onChange,
  type = "text",
  icon: IconComp,
  id,
  sectionId,
  error,
}) {
  const [focused, setFocused] = useState(false);
  const style = error
    ? { ...inputStyle(focused), ...fieldErrorBorder }
    : inputStyle(focused);
  return (
    <div data-field-wrapper data-field-id={id}>
      {label && (
        <FL required={required} optional={optional} fieldId={id} sectionId={sectionId}>
          {label}
        </FL>
      )}
      <div style={{ position: "relative" }}>
        {IconComp && (
          <div
            style={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
              color: focused ? "#6366f1" : "#64748b",
              pointerEvents: "none",
              display: "flex",
            }}
          >
            <IconComp size={13} />
          </div>
        )}
        <input
          id={id}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          style={{ ...style, paddingLeft: IconComp ? 32 : 12 }}
        />
      </div>
      {error && (
        <p style={{ fontSize: 10, color: "#b45309", marginTop: 4 }}>{error}</p>
      )}
    </div>
  );
}

function FTextarea({
  label,
  placeholder,
  required,
  optional,
  value,
  onChange,
  rows = 3,
  icon: IconComp,
  id,
  sectionId,
  error,
}) {
  const [focused, setFocused] = useState(false);
  const style = error
    ? { ...inputStyle(focused), ...fieldErrorBorder }
    : inputStyle(focused);
  return (
    <div data-field-wrapper data-field-id={id}>
      {label && (
        <FL required={required} optional={optional} fieldId={id} sectionId={sectionId}>
          {label}
        </FL>
      )}
      <div style={{ position: "relative" }}>
        {IconComp && (
          <div
            style={{
              position: "absolute",
              left: 10,
              top: 11,
              color: focused ? "#6366f1" : "#64748b",
              pointerEvents: "none",
              display: "flex",
            }}
          >
            <IconComp size={13} />
          </div>
        )}
        <textarea
          id={id}
          rows={rows}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          style={{
            ...style,
            paddingLeft: IconComp ? 32 : 12,
            resize: "vertical",
          }}
        />
      </div>
      {error && (
        <p style={{ fontSize: 10, color: "#b45309", marginTop: 4 }}>{error}</p>
      )}
    </div>
  );
}

function FSelect({
  label,
  options,
  required,
  optional,
  value,
  onChange,
  icon: IconComp,
  id,
  sectionId,
  error,
}) {
  const [focused, setFocused] = useState(false);
  const style = error
    ? { ...inputStyle(focused), ...fieldErrorBorder }
    : inputStyle(focused);
  return (
    <div data-field-wrapper data-field-id={id}>
      {label && (
        <FL required={required} optional={optional} fieldId={id} sectionId={sectionId}>
          {label}
        </FL>
      )}
      <div style={{ position: "relative" }}>
        {IconComp && (
          <div
            style={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
              color: focused ? "#6366f1" : "#64748b",
              pointerEvents: "none",
              zIndex: 1,
              display: "flex",
            }}
          >
            <IconComp size={13} />
          </div>
        )}
        <select
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            ...style,
            paddingLeft: IconComp ? 32 : 12,
            paddingRight: 32,
            appearance: "none",
            cursor: "pointer",
            color: value ? "#0f172a" : "#64748b",
          }}
        >
          <option value="">Select an option…</option>
          {options.map((o) => (
            <option key={o} value={o} style={{ color: "#0f172a" }}>
              {o}
            </option>
          ))}
        </select>
        <div
          style={{
            position: "absolute",
            right: 10,
            top: "50%",
            transform: "translateY(-50%)",
            color: "#475569",
            pointerEvents: "none",
            display: "flex",
          }}
        >
          <ChevronDown size={13} />
        </div>
      </div>
      {error && (
        <p style={{ fontSize: 10, color: "#b45309", marginTop: 4 }}>{error}</p>
      )}
    </div>
  );
}

function FMultiSelect({
  label,
  id,
  sectionId,
  options,
  required,
  optional,
  value = [],
  onChange,
  error,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef();

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const toggleOption = (option) =>
    value.includes(option)
      ? onChange(value.filter((item) => item !== option))
      : onChange([...value, option]);

  const filteredOptions = options.filter((option) =>
    option.toLowerCase().includes(q.toLowerCase()),
  );

  return (
    <div
      ref={ref}
      data-field-wrapper
      data-field-id={id}
      style={{ position: "relative" }}
    >
      {label && (
        <FL required={required} optional={optional} fieldId={id} sectionId={sectionId}>
          {label}
        </FL>
      )}
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        style={{
          ...inputStyle(false),
          width: "100%",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          minHeight: 42,
        }}
      >
        <span style={{ color: value.length ? "#0f172a" : "#64748b" }}>
          {value.length ? value.join(", ") : "Select options…"}
        </span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            zIndex: 20,
            left: 0,
            right: 0,
            marginTop: 6,
            padding: 12,
            borderRadius: 12,
            background: "#ffffff",
            border: "1px solid rgba(99,102,241,0.18)",
            boxShadow: "0 16px 40px rgba(15,23,42,0.12)",
          }}
        >
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter options…"
            style={{
              width: "100%",
              marginBottom: 10,
              ...inputStyle(false),
            }}
          />
          <div
            style={{
              maxHeight: 200,
              overflowY: "auto",
              gap: 6,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {filteredOptions.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => toggleOption(option)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 10px",
                  borderRadius: 10,
                  border: "1px solid rgba(99,102,241,0.12)",
                  background: value.includes(option) ? "#eef2ff" : "#fff",
                  cursor: "pointer",
                  color: "#0f172a",
                }}
              >
                <span>{option}</span>
                {value.includes(option) && <Check size={14} color="#6366f1" />}
              </button>
            ))}
          </div>
        </div>
      )}
      {error && (
        <p style={{ fontSize: 10, color: "#b45309", marginTop: 4 }}>{error}</p>
      )}
    </div>
  );
}

// ─── Toggle Row ───────────────────────────────────────────────────────────────
function ToggleRow({
  label,
  note,
  value,
  onChange,
  noteValue,
  onNoteChange,
  accent = "#6366f1",
  fieldId,
  error,
}) {
  return (
    <div
      id={fieldId}
      data-field-wrapper
      data-field-id={fieldId}
      style={{
        borderRadius: 12,
        border: error ? "1px solid #fca5a5" : "1px solid rgba(99,102,241,0.12)",
        overflow: "hidden",
        background: "#ffffff",
        transition: "all 0.2s ease",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "12px 14px",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            style={{
              fontSize: 12.5,
              fontWeight: 500,
              color: "#0f172a",
              lineHeight: 1.45,
            }}
          >
            {label}
          </p>
          {note && (
            <p
              style={{
                fontSize: 10,
                color: "#475569",
                marginTop: 2,
                lineHeight: 1.4,
              }}
            >
              {note}
            </p>
          )}
        </div>
        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
          {["Yes", "No"].map((opt) => {
            const active = value === opt;
            const isYes = opt === "Yes";
            const aColor = isYes ? accent : "#475569";
            return (
              <button
                key={opt}
                onClick={() => onChange(active ? "" : opt)}
                style={{
                  padding: "6px 14px",
                  borderRadius: 9,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: "pointer",
                  background: active ? aColor : "#f8fafc",
                  color: active ? "#ffffff" : "#475569",
                  border: `1.5px solid ${active ? aColor : "rgba(99,102,241,0.12)"}`,
                  boxShadow: active ? `0 2px 8px ${aColor}35` : "none",
                  transition: "all 0.15s",
                }}
              >
                {opt}
              </button>
            );
          })}
        </div>
      </div>
      {onNoteChange && (
        <div style={{ padding: "0 14px 12px" }}>
          <input
            value={noteValue || ""}
            onChange={(e) => onNoteChange(e.target.value)}
            placeholder="Add implementation notes or evidence…"
            style={{ ...inputStyle(), fontSize: 11, borderStyle: "dashed" }}
          />
        </div>
      )}
      {error && (
        <p
          style={{
            fontSize: 10,
            color: "#b45309",
            padding: "0 14px 10px",
            margin: 0,
          }}
        >
          {error}
        </p>
      )}
    </div>
  );
}

// ─── Dynamic Table ────────────────────────────────────────────────────────────
function DynTable({ columns, rows, onChange, accent = "#6366f1", error }) {
  const add = () => {
    const r = {};
    columns.forEach((c) => (r[c.key] = ""));
    onChange([...rows, r]);
  };
  const del = (i) => onChange(rows.filter((_, idx) => idx !== i));
  const upd = (i, k, v) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  return (
    <div id="rows" data-field-wrapper data-field-id="rows">
      {error && (
        <p style={{ fontSize: 10, color: "#b45309", marginBottom: 8 }}>
          {error}
        </p>
      )}
      <div
        style={{
          borderRadius: 12,
          border: error
            ? "1px solid #fca5a5"
            : "1px solid rgba(99,102,241,0.14)",
          overflow: "hidden",
          boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
        }}
      >
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              minWidth: "max-content",
              borderCollapse: "collapse",
            }}
          >
            <thead>
              <tr
                style={{
                  background: "#f8fafc",
                  borderBottom: "1px solid rgba(99,102,241,0.1)",
                }}
              >
                {columns.map((c) => (
                  <th
                    key={c.key}
                    style={{
                      padding: "10px 14px",
                      textAlign: "left",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#1e293b",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {c.label}
                  </th>
                ))}
                <th style={{ width: 36 }} />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td
                    colSpan={columns.length + 1}
                    style={{ padding: "32px 14px", textAlign: "center" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <p style={{ fontSize: 12, color: "#64748b" }}>
                        No entries yet — click{" "}
                        <strong style={{ color: "#334155" }}>Add Row</strong>
                      </p>
                    </div>
                  </td>
                </tr>
              )}
              {rows.map((row, i) => (
                <tr
                  key={i}
                  style={{
                    borderBottom: "1px solid rgba(99,102,241,0.07)",
                    background: i % 2 === 0 ? "#ffffff" : "#fafafa",
                    transition: "background 0.15s",
                  }}
                >
                  {columns.map((c) => (
                    <td key={c.key} style={{ padding: "8px 10px" }}>
                      <input
                        type={c.type || "text"}
                        value={row[c.key] || ""}
                        onChange={(e) => upd(i, c.key, e.target.value)}
                        placeholder={c.placeholder || c.label}
                        style={{
                          minWidth: c.minW || "100px",
                          fontSize: 12,
                          color: "#0f172a",
                          background: "transparent",
                          border: "1.5px solid transparent",
                          borderRadius: 8,
                          padding: "5px 8px",
                          outline: "none",
                          fontFamily: "inherit",
                          transition: "all 0.15s",
                          cursor: "text",
                          ":focus": { background: "#fff" },
                        }}
                        onFocus={(e) => {
                          e.target.style.background = "#ffffff";
                          e.target.style.borderColor = "rgba(99,102,241,0.28)";
                          e.target.style.boxShadow =
                            "0 0 0 3px rgba(99,102,241,0.08)";
                        }}
                        onBlur={(e) => {
                          e.target.style.background = "transparent";
                          e.target.style.borderColor = "transparent";
                          e.target.style.boxShadow = "none";
                        }}
                      />
                    </td>
                  ))}
                  <td style={{ padding: "8px 10px", textAlign: "center" }}>
                    <button
                      onClick={() => del(i)}
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 8,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        color: "#cbd5e1",
                        transition: "all 0.15s",
                        margin: "auto",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#fee2e2";
                        e.currentTarget.style.color = "#ef4444";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.color = "#cbd5e1";
                      }}
                    >
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <button
        onClick={add}
        style={{
          marginTop: 10,
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 16px",
          borderRadius: 10,
          fontSize: 11,
          fontWeight: 600,
          cursor: "pointer",
          color: accent,
          background: `${accent}0c`,
          border: `1.5px dashed ${accent}45`,
          transition: "all 0.15s",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = `${accent}18`;
          e.currentTarget.style.borderColor = accent;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = `${accent}0c`;
          e.currentTarget.style.borderColor = `${accent}45`;
        }}
      >
        <div
          style={{
            width: 20,
            height: 20,
            borderRadius: 6,
            background: `${accent}20`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Plus size={11} color={accent} />
        </div>
        Add Row
      </button>
    </div>
  );
}

// ─── Field grid helpers ───────────────────────────────────────────────────────
const G2 = ({ children }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))",
      gap: 14,
    }}
  >
    {children}
  </div>
);

function getOrderedSectionFieldIds(sectionId, defaultIds, schemaFields) {
  const sectionFields = schemaFields.filter(
    (f) => f.section_id === sectionId && f.is_active !== false,
  );
  if (!sectionFields.length) return defaultIds;
  const ordered = [...sectionFields]
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((f) => f.external_id);
  const seen = new Set(ordered);
  defaultIds.forEach((id) => {
    if (!seen.has(id)) ordered.push(id);
  });
  return ordered;
}

function isWideRopaField(sectionId, id, schemaFields) {
  const field = schemaField(schemaFields, sectionId, id);
  return field?.field_type === "textarea";
}

function renderOrderedScalarSection(
  sectionId,
  defaultIds,
  builders,
  schemaFields,
  d,
  set,
  errors,
) {
  const rf = (id, el) => renderRopaField(sectionId, id, el, schemaFields);
  const ids = getOrderedSectionFieldIds(sectionId, defaultIds, schemaFields);
  const rendered = [];
  let pair = [];

  const flushPair = () => {
    if (pair.length === 1) {
      rendered.push(<div key={pair[0].key}>{pair[0].node}</div>);
    } else if (pair.length === 2) {
      rendered.push(
        <G2 key={`${pair[0].key}-${pair[1].key}`}>
          {pair[0].node}
          {pair[1].node}
        </G2>,
      );
    }
    pair = [];
  };

  ids.forEach((id) => {
    let node = null;
    if (builders[id]) {
      node = rf(id, builders[id]);
    } else {
      const field = schemaField(schemaFields, sectionId, id);
      if (field) {
        node = (
          <DynamicField field={field} d={d} set={set} errors={errors} />
        );
      }
    }
    if (!node) return;

    if (isWideRopaField(sectionId, id, schemaFields)) {
      flushPair();
      rendered.push(<div key={id}>{node}</div>);
      return;
    }

    pair.push({ key: id, node });
    if (pair.length === 2) flushPair();
  });
  flushPair();

  return rendered;
}

// ─── Info Banner ──────────────────────────────────────────────────────────────
function InfoBanner({ children, variant = "blue" }) {
  const cfg = {
    blue: {
      bg: "#eff6ff",
      border: "rgba(99,102,241,0.22)",
      color: "#1e40af",
      dot: "#3b82f6",
    },
    amber: {
      bg: "#fffbeb",
      border: "rgba(251,191,36,0.4)",
      color: "#92400e",
      dot: "#f59e0b",
    },
  }[variant];
  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        padding: "11px 14px",
        borderRadius: 10,
        background: cfg.bg,
        border: `1px solid ${cfg.border}`,
        fontSize: 12,
        color: cfg.color,
        lineHeight: 1.5,
      }}
    >
      <div
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: cfg.dot,
          flexShrink: 0,
          marginTop: 5,
        }}
      />
      <span>{children}</span>
    </div>
  );
}

// ─── Section content panels ───────────────────────────────────────────────────
function OrgPanel({ d, set, errors = {}, schemaFields = [] }) {
  const defaultIds = [
    "name",
    "role",
    "address",
    "email",
    "phone",
    "grievance",
    "sdf",
    "lastUpdated",
  ];
  const builders = {
    name: (
      <FInput
        label="Organisation Name"
        id="name"
        required
        icon={Building2}
        placeholder="e.g., Acme Technologies Pvt. Ltd."
        value={d.name || ""}
        onChange={set("name")}
        error={errors.name}
      />
    ),
    role: (
      <FSelect
        label="Organisation Role"
        id="role"
        required
        icon={Tag}
        options={["Data Fiduciary", "Data Processor", "Both"]}
        value={d.role || ""}
        onChange={set("role")}
        error={errors.role}
      />
    ),
    address: (
      <FTextarea
        label="Registered Address (India)"
        id="address"
        required
        icon={MapPin}
        placeholder="123 MG Road, Bengaluru, Karnataka – 560001"
        rows={2}
        value={d.address || ""}
        onChange={set("address")}
        error={errors.address}
      />
    ),
    email: (
      <FInput
        label="Contact Email"
        id="email"
        required
        icon={Mail}
        type="email"
        placeholder="dpo@company.in"
        value={d.email || ""}
        onChange={set("email")}
        error={errors.email}
      />
    ),
    phone: (
      <FInput
        label="Contact Phone"
        id="phone"
        optional
        icon={Phone}
        type="tel"
        placeholder="+91 98765 43210"
        value={d.phone || ""}
        onChange={set("phone")}
      />
    ),
    grievance: (
      <FInput
        label="Grievance Officer"
        id="grievance"
        required
        icon={Users}
        placeholder="Full name of Grievance Officer"
        value={d.grievance || ""}
        onChange={set("grievance")}
        error={errors.grievance}
      />
    ),
    sdf: (
      <FSelect
        label="Significant Data Fiduciary?"
        id="sdf"
        required
        icon={Shield}
        options={["Yes", "No", "Under Assessment"]}
        value={d.sdf || ""}
        onChange={set("sdf")}
        error={errors.sdf}
      />
    ),
    lastUpdated: (
      <FInput
        label="Last Updated"
        id="lastUpdated"
        required
        icon={Calendar}
        type="date"
        value={d.lastUpdated || ""}
        onChange={set("lastUpdated")}
        error={errors.lastUpdated}
      />
    ),
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {renderOrderedScalarSection(
        "org",
        defaultIds,
        builders,
        schemaFields,
        d,
        set,
        errors,
      )}
    </div>
  );
}

function ActivityPanel({ d, set, errors = {}, schemaFields = [] }) {
  const defaultIds = ["name", "dept", "purpose", "desc"];
  const builders = {
    name: (
      <FInput
        label="Processing Activity Name"
        id="name"
        required
        icon={Settings2}
        placeholder="e.g., Customer Onboarding KYC"
        value={d.name || ""}
        onChange={set("name")}
        error={errors.name}
      />
    ),
    dept: (
      <FInput
        label="Department / Business Function"
        id="dept"
        required
        icon={Building2}
        placeholder="e.g., Operations / Compliance"
        value={d.dept || ""}
        onChange={set("dept")}
        error={errors.dept}
      />
    ),
    purpose: (
      <FInput
        label="Purpose of Processing"
        id="purpose"
        required
        icon={Target}
        placeholder="Describe the primary purpose of this activity"
        value={d.purpose || ""}
        onChange={set("purpose")}
        error={errors.purpose}
      />
    ),
    desc: (
      <FTextarea
        label="Detailed Description"
        id="desc"
        required
        rows={4}
        placeholder="Comprehensive description of how personal data is processed, which systems are involved, and the intended outcome."
        value={d.desc || ""}
        onChange={set("desc")}
        error={errors.desc}
      />
    ),
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {renderOrderedScalarSection(
        "activity",
        defaultIds,
        builders,
        schemaFields,
        d,
        set,
        errors,
      )}
    </div>
  );
}

function LegalPanel({ d, set, accent, errors = {}, schemaFields = [] }) {
  const rf = (id, el) => renderRopaField("legal", id, el, schemaFields);
  const items = [
    {
      key: "consent",
      label: "Consent",
      note: "Explicit consent obtained from data principal",
    },
    {
      key: "employment",
      label: "Deemed Consent — Employment",
      note: "Processing for employment-related purposes",
    },
    {
      key: "legalObligation",
      label: "Deemed Consent — Legal Obligation",
      note: "Required by law, regulation or court order",
    },
    {
      key: "publicInterest",
      label: "Deemed Consent — Public Interest",
      note: "Sovereign functions, public health, safety",
    },
  ];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {orderSectionItems(items, "legal", schemaFields).map((b) => (
        <div key={b.key}>
          {rf(
            b.key,
            <ToggleRow
              label={b.label}
              note={b.note}
              accent={accent}
              fieldId={b.key}
              value={d[b.key] || ""}
              onChange={(v) => set(b.key)(v)}
              noteValue={d[`${b.key}Note`] || ""}
              onNoteChange={(v) => set(`${b.key}Note`)(v)}
              error={errors[b.key]}
            />,
          )}
        </div>
      ))}
    </div>
  );
}

function PrincipalsPanel({ d, set, accent, errors = {}, schemaFields = [] }) {
  const rf = (id, el) => renderRopaField("principals", id, el, schemaFields);
  const items = [
    { key: "customers", label: "Customers", note: null },
    { key: "employees", label: "Employees", note: null },
    { key: "applicants", label: "Applicants / Job Seekers", note: null },
    { key: "vendors", label: "Vendors / Suppliers", note: null },
    {
      key: "minors",
      label: "Minors (under 18)",
      note: "Parental / guardian consent required under DPDP Act",
    },
  ];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {orderSectionItems(items, "principals", schemaFields).map((c) => (
        <div key={c.key}>
          {rf(
            c.key,
            <ToggleRow
              label={c.label}
              note={c.note}
              accent={accent}
              fieldId={c.key}
              value={d[c.key] || ""}
              onChange={(v) => set(c.key)(v)}
              noteValue={d[`${c.key}Note`] || ""}
              onNoteChange={(v) => set(`${c.key}Note`)(v)}
              error={errors[c.key]}
            />,
          )}
        </div>
      ))}
    </div>
  );
}

function PersonalDataPanel({ d, set, accent, errors = {}, schemaFields = [] }) {
  const rf = (id, el) => renderRopaField("personaldata", id, el, schemaFields);
  const items = [
    {
      key: "identity",
      label: "Identity Data",
      note: "Name, DOB, PAN, Aadhaar, Passport number",
    },
    {
      key: "contact",
      label: "Contact Data",
      note: "Email address, phone number, postal address",
    },
    {
      key: "financial",
      label: "Financial Data",
      note: "Bank details, salary, transaction history",
    },
    {
      key: "auth",
      label: "Authentication Data",
      note: "Passwords, OTPs, biometrics, security keys",
    },
    {
      key: "usageDevice",
      label: "Usage / Device Data",
      note: "IP address, cookies, device IDs, activity logs",
    },
  ];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {orderSectionItems(items, "personaldata", schemaFields).map((c) => (
        <div key={c.key}>
          {rf(
            c.key,
            <ToggleRow
              label={c.label}
              note={c.note}
              accent={accent}
              fieldId={c.key}
              value={d[c.key] || ""}
              onChange={(v) => set(c.key)(v)}
              noteValue={d[`${c.key}Note`] || ""}
              onNoteChange={(v) => set(`${c.key}Note`)(v)}
              error={errors[c.key]}
            />,
          )}
        </div>
      ))}
    </div>
  );
}

function LifecyclePanel({ d, set, errors = {}, schemaFields = [] }) {
  const defaultIds = ["source", "storage", "method", "access", "sharing"];
  const builders = {
    source: (
      <FTextarea
        label="Collection Source"
        id="source"
        required
        icon={Server}
        rows={2}
        placeholder="Web forms, mobile app, third-party APIs…"
        value={d.source || ""}
        onChange={set("source")}
        error={errors.source}
      />
    ),
    storage: (
      <FTextarea
        label="Storage Location"
        id="storage"
        required
        icon={Database}
        rows={2}
        placeholder="AWS Mumbai (ap-south-1), on-premise datacenter…"
        value={d.storage || ""}
        onChange={set("storage")}
        error={errors.storage}
      />
    ),
    method: (
      <FTextarea
        label="Processing Method"
        id="method"
        required
        icon={Settings2}
        rows={2}
        placeholder="Automated ML pipeline, manual review, batch processing…"
        value={d.method || ""}
        onChange={set("method")}
        error={errors.method}
      />
    ),
    access: (
      <FTextarea
        label="Access Control Measures"
        id="access"
        required
        icon={Key}
        rows={2}
        placeholder="Role-based access, MFA, least privilege, audit logging…"
        value={d.access || ""}
        onChange={set("access")}
        error={errors.access}
      />
    ),
    sharing: (
      <FSelect
        label="Data Sharing Occurs?"
        id="sharing"
        required
        icon={Share2}
        options={["Yes", "No", "Conditionally"]}
        value={d.sharing || ""}
        onChange={set("sharing")}
        error={errors.sharing}
      />
    ),
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {renderOrderedScalarSection(
        "lifecycle",
        defaultIds,
        builders,
        schemaFields,
        d,
        set,
        errors,
      )}
    </div>
  );
}

function SharingPanel({ d, set, accent, errors = {} }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <InfoBanner variant="amber">
        List all third parties, sub-processors, or government bodies that
        receive personal data. Include all cross-border transfers.
      </InfoBanner>
      <DynTable
        accent={accent}
        columns={[
          {
            key: "recipient",
            label: "Recipient Name",
            minW: "140px",
            placeholder: "e.g., AWS India Pvt. Ltd.",
          },
          {
            key: "role",
            label: "Role / Type",
            minW: "120px",
            placeholder: "e.g., Sub-processor",
          },
          {
            key: "purpose",
            label: "Purpose",
            minW: "160px",
            placeholder: "e.g., Cloud hosting",
          },
          {
            key: "location",
            label: "Location / Region",
            minW: "120px",
            placeholder: "e.g., India / EU",
          },
        ]}
        rows={d.rows || []}
        onChange={(v) => set("rows")(v)}
        error={errors.rows}
      />
    </div>
  );
}

function RetentionPanel({ d, set, accent, errors = {} }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <InfoBanner variant="blue">
        Specify retention periods for each data category. The DPDP Act mandates
        erasure once the specified purpose is fulfilled or consent is withdrawn.
      </InfoBanner>
      <DynTable
        accent={accent}
        columns={[
          {
            key: "category",
            label: "Data Category",
            minW: "140px",
            placeholder: "e.g., Financial Records",
          },
          {
            key: "period",
            label: "Retention Period",
            minW: "120px",
            placeholder: "e.g., 7 years",
          },
          {
            key: "trigger",
            label: "Deletion Trigger",
            minW: "140px",
            placeholder: "e.g., Account closure",
          },
          {
            key: "method",
            label: "Deletion Method",
            minW: "130px",
            placeholder: "e.g., Secure wipe",
          },
        ]}
        rows={d.rows || []}
        onChange={(v) => set("rows")(v)}
        error={errors.rows}
      />
    </div>
  );
}

function SecurityPanel({ d, set, accent, errors = {}, schemaFields = [] }) {
  const rf = (id, el) => renderRopaField("security", id, el, schemaFields);
  const items = [
    {
      key: "encryption",
      label: "Encryption",
      note: "AES-256 at rest, TLS 1.3 in transit",
    },
    {
      key: "accessCtrl",
      label: "Access Controls",
      note: "RBAC, MFA, least-privilege enforcement",
    },
    {
      key: "logging",
      label: "Logging & Monitoring",
      note: "SIEM integration, audit logs, anomaly detection",
    },
    {
      key: "incident",
      label: "Incident Response Plan",
      note: "Documented IRP with Data Protection Board escalation",
    },
    {
      key: "backup",
      label: "Backup & Disaster Recovery",
      note: "Regular backups with tested DR procedures",
    },
  ];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {orderSectionItems(items, "security", schemaFields).map((m) => (
        <div key={m.key}>
          {rf(
            m.key,
            <ToggleRow
              label={m.label}
              note={m.note}
              accent={accent}
              fieldId={m.key}
              value={d[m.key] || ""}
              onChange={(v) => set(m.key)(v)}
              noteValue={d[`${m.key}Note`] || ""}
              onNoteChange={(v) => set(`${m.key}Note`)(v)}
              error={errors[m.key]}
            />,
          )}
        </div>
      ))}
    </div>
  );
}

function RightsPanel({ d, set, accent, errors = {} }) {
  const def = [
    { right: "Access", process: "", sla: "30" },
    { right: "Correction", process: "", sla: "30" },
    { right: "Erasure", process: "", sla: "30" },
    { right: "Consent Withdrawal", process: "", sla: "7" },
    { right: "Grievance Redressal", process: "", sla: "30" },
  ];
  return (
    <DynTable
      accent={accent}
      columns={[
        {
          key: "right",
          label: "Right (DPDP Act)",
          minW: "160px",
          placeholder: "e.g., Access",
        },
        {
          key: "process",
          label: "Process / Mechanism",
          minW: "220px",
          placeholder: "Describe fulfilment process",
        },
        { key: "sla", label: "SLA (days)", minW: "90px", placeholder: "30" },
      ]}
      rows={d.rows?.length ? d.rows : def}
      onChange={(v) => set("rows")(v)}
      error={errors.rows}
    />
  );
}

function IncidentPanel({ d, set, errors = {}, schemaFields = [] }) {
  const defaultIds = [
    "identification",
    "dpbNotification",
    "timeline",
    "communication",
  ];
  const builders = {
    identification: (
      <FTextarea
        label="Breach Identification Process"
        id="identification"
        required
        icon={Search}
        rows={3}
        placeholder="Describe how breaches are detected — SIEM alerts, employee reporting, third-party notification…"
        value={d.identification || ""}
        onChange={set("identification")}
        error={errors.identification}
      />
    ),
    dpbNotification: (
      <FSelect
        label="DPB Notification Required?"
        id="dpbNotification"
        required
        icon={Bell}
        options={[
          "Yes — always",
          "Yes — if significant",
          "No",
          "Under Assessment",
        ]}
        value={d.dpbNotification || ""}
        onChange={set("dpbNotification")}
        error={errors.dpbNotification}
      />
    ),
    timeline: (
      <FInput
        label="Notification Timeline"
        id="timeline"
        required
        icon={Clock}
        placeholder="e.g., Within 72 hours of detection"
        value={d.timeline || ""}
        onChange={set("timeline")}
        error={errors.timeline}
      />
    ),
    communication: (
      <FTextarea
        label="User Communication Plan"
        id="communication"
        required
        icon={Bell}
        rows={2}
        placeholder="How and when affected data principals will be notified…"
        value={d.communication || ""}
        onChange={set("communication")}
        error={errors.communication}
      />
    ),
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {renderOrderedScalarSection(
        "incident",
        defaultIds,
        builders,
        schemaFields,
        d,
        set,
        errors,
      )}
    </div>
  );
}

function ApprovalPanel({ d, set, errors = {} }) {
  const def = [
    { role: "Process Owner", name: "", signature: "", date: "" },
    { role: "Grievance Officer / DPO", name: "", signature: "", date: "" },
    { role: "Legal / Compliance Head", name: "", signature: "", date: "" },
  ];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <DynTable
        accent="#059669"
        columns={[
          {
            key: "role",
            label: "Role",
            minW: "160px",
            placeholder: "e.g., Process Owner",
          },
          {
            key: "name",
            label: "Full Name",
            minW: "150px",
            placeholder: "Full legal name",
          },
          {
            key: "signature",
            label: "Signature Ref.",
            minW: "130px",
            placeholder: "Doc reference or ID",
          },
          {
            key: "date",
            label: "Date",
            minW: "110px",
            placeholder: "YYYY-MM-DD",
            type: "date",
          },
        ]}
        rows={d.rows?.length ? d.rows : def}
        onChange={(v) => set("rows")(v)}
        error={errors.rows}
      />
      <div
        style={{
          padding: "16px",
          borderRadius: 12,
          background: "#ecfdf5",
          border: "1px solid #a7f3d0",
          display: "flex",
          gap: 12,
          alignItems: "flex-start",
        }}
      >
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 10,
            background: "#059669",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            boxShadow: "0 2px 8px rgba(5,150,105,0.3)",
          }}
        >
          <ShieldCheck size={16} color="#ffffff" />
        </div>
        <div>
          <p
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: "#065f46",
              marginBottom: 4,
            }}
          >
            Certification Statement
          </p>
          <p style={{ fontSize: 11.5, color: "#047857", lineHeight: 1.6 }}>
            By completing this ROPA record, the undersigned confirm that the
            information contained herein is accurate, complete, and reflective
            of the actual data processing activities as of the date specified.
            This document is prepared in compliance with the{" "}
            <strong>Digital Personal Data Protection Act, 2023 (India)</strong>.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Arc Gauge (identical to GAP) ────────────────────────────────────────────
function ArcGauge({
  value,
  size = 68,
  stroke = 7,
  color = "#6366f1",
  bgStrokeColor = "rgba(71, 85, 105, 0.15)",
}) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (value / 100) * circ;
  return (
    <svg
      width={size}
      height={size}
      style={{ transform: "rotate(-90deg)", display: "block" }}
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={bgStrokeColor}
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeDasharray={circ}
        strokeDashoffset={offset}
        strokeLinecap="round"
        style={{
          transition: "stroke-dashoffset 0.9s cubic-bezier(0.4,0,0.2,1)",
        }}
      />
    </svg>
  );
}

// ─── Sidebar content (same pattern as GAP) ───────────────────────────────────
function SidebarContent({ step, data, onGoTo }) {
  const totalFilled = SECTIONS.filter((s) =>
    isSectionComplete(s.id, data),
  ).length;
  const pct = Math.round((totalFilled / SECTIONS.length) * 100);
  const fillColor = pct >= 75 ? "#10b981" : pct >= 40 ? "#f59e0b" : "#6366f1";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          padding: "12px 12px 16px",
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(99,102,241,0.4) #e2e8f0",
        }}
      >
        <div
          style={{
            background: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: 12,
            padding: "10px 12px",
            marginBottom: 8,
            boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 8,
            }}
          >
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: "#1e293b",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}
            >
              Completion
            </span>
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                padding: "2px 8px",
                borderRadius: 20,
                background: fillColor + "15",
                color: fillColor,
                border: `1px solid ${fillColor}30`,
              }}
            >
              {pct >= 75 ? "On Track" : pct >= 40 ? "In Progress" : "Started"}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ position: "relative", flexShrink: 0 }}>
              <ArcGauge value={pct} size={52} stroke={6} color="#10b981" />
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                }}
              >
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 800,
                    color: "#0f172a",
                    lineHeight: 1,
                  }}
                >
                  {pct}%
                </span>
              </div>
            </div>
            <div style={{ flex: 1 }}>
              {[
                { l: "Filled", v: totalFilled, c: "#10b981" },
                {
                  l: "Pending",
                  v: SECTIONS.length - totalFilled,
                  c: "#475569",
                },
                { l: "Sections", v: SECTIONS.length, c: "#6366f1" },
              ].map((x) => (
                <div
                  key={x.l}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginBottom: 4,
                  }}
                >
                  <div
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: x.c,
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ fontSize: 10, color: "#334155", flex: 1 }}>
                    {x.l}
                  </span>
                  <span
                    style={{ fontSize: 11, fontWeight: 700, color: "#0f172a" }}
                  >
                    {x.v}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div
            style={{
              marginTop: 8,
              height: 3,
              borderRadius: 9,
              background: "#f1f5f9",
              overflow: "hidden",
              display: "flex",
            }}
          >
            <div
              style={{
                width: `${pct}%`,
                background: `linear-gradient(90deg,#6366f1,${fillColor})`,
                height: "100%",
                transition: "width 0.7s ease",
                borderRadius: 9,
              }}
            />
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 5,
              fontSize: 10,
              color: "#334155",
            }}
          >
            <span>{totalFilled} filled</span>
            <span>{SECTIONS.length} total</span>
          </div>
        </div>
        {data && (
          <FormScorePanel
            scorePath="/ropa/score"
            formData={data}
            levelLabel="Compliance Level"
            accent="#10b981"
            variant="sidebar"
          />
        )}
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: "#0f172a",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            padding: "2px 4px 8px",
          }}
        >
          Sections
        </div>

        {SECTIONS.map((s, i) => {
          const filled = isSectionComplete(s.id, data);
          const active = i === step;
          const SIcon = s.Icon;
          return (
            <button
              key={s.id}
              onClick={() => onGoTo(i)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 10px",
                borderRadius: 12,
                border: `1px solid ${active ? s.accent + "35" : "transparent"}`,
                background: active
                  ? `linear-gradient(135deg,${s.accent}18 0%,${s.accent}0a 100%)`
                  : "transparent",
                cursor: "pointer",
                transition: "all 0.15s ease",
                textAlign: "left",
                marginBottom: 2,
              }}
            >
              <div
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 9,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: active
                    ? `linear-gradient(135deg,${s.accent}cc,${s.accent}66)`
                    : filled
                      ? "#ecfdf5"
                      : "linear-gradient(135deg,#f1f5f9,#e8eaf6)",
                  border: `1px solid ${active ? s.accent + "50" : filled ? "#a7f3d0" : "rgba(99,102,241,0.1)"}`,
                  boxShadow: active ? `0 2px 8px ${s.accent}35` : "none",
                }}
              >
                {filled && !active ? (
                  <Check size={12} color="#059669" />
                ) : (
                  <SIcon size={13} color={active ? "#fff" : "#0f172a"} />
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#0f172a",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {s.title}
                </div>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 500,
                    color: active ? s.accent : "#1e293b",
                    marginTop: 1,
                  }}
                >
                  {s.num} · {filled ? "Filled" : "Pending"}
                </div>
              </div>
              {filled && !active && (
                <CheckCircle2
                  size={12}
                  color="#10b981"
                  style={{ flexShrink: 0 }}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Result screen ────────────────────────────────────────────────────────────
function ResultScreen({ data, onReset }) {
  const [exporting, setExporting] = useState(false);
  const totalFilled = SECTIONS.filter((s) =>
    isSectionComplete(s.id, data),
  ).length;
  const pct = Math.round((totalFilled / SECTIONS.length) * 100);
  const scoreColor = pct >= 75 ? "#10b981" : pct >= 50 ? "#f59e0b" : "#ef4444";
  const scoreLabel =
    pct >= 75
      ? "Comprehensive Record"
      : pct >= 50
        ? "Partially Complete"
        : "Needs Completion";

  const handleExport = async () => {
    setExporting(true);
    try {
      const payload = ROPA_CONFIG.buildExportSections(
        {
          form_data: data,
          progress_pct: pct,
          sections_filled: totalFilled,
          sections_total: SECTIONS.length,
          org_name: data.org?.name || "",
          activity_name: data.activity?.name || "",
          created_at: new Date().toISOString(),
        },
        { forExcel: false },
      );

      await exportSubmissionPdf({
        title: data.org?.name || "ROPA Submission",
        subtitle: `ROPA · ${new Date().toLocaleDateString()}`,
        module: "ROPA",
        organization: data.org?.name || "",
        sections: payload,
      });
    } catch (err) {
      showError(err?.message || "Failed to export PDF");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div
      style={{
        flex: 1,
        height: "100%",
        overflowY: "auto",
        background: "#ffffff",
        fontFamily: "'DM Sans',system-ui,sans-serif",
        scrollbarWidth: "thin",
        scrollbarColor: "rgba(99,102,241,0.3) #e2e8f0",
      }}
    >
      <div
        style={{
          minHeight: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 20,
        }}
      >
        <div style={{ maxWidth: 580, width: "100%" }}>
          <div
            style={{
              borderRadius: 24,
              padding: "32px 24px",
              textAlign: "center",
              background: "#ffffff",
              border: "1px solid rgba(99,102,241,0.15)",
              marginBottom: 16,
            }}
          >
            <div
              style={{
                width: 60,
                height: 60,
                borderRadius: 18,
                background:
                  "linear-gradient(135deg,#6366f1 0%,#8b5cf6 50%,#a855f7 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
                boxShadow:
                  "0 8px 32px rgba(99,102,241,0.4),0 4px 12px rgba(139,92,246,0.3)",
              }}
            >
              <ShieldCheck size={26} color="#fff" />
            </div>
            <h2
              style={{
                fontSize: 22,
                fontWeight: 800,
                color: "#0f172a",
                marginBottom: 4,
              }}
            >
              ROPA Submitted
            </h2>
            <p style={{ fontSize: 12, color: "#334155", marginBottom: 24 }}>
              Digital Personal Data Protection Act, 2023 — Record of Processing
              Activities
            </p>
            <div
              style={{
                position: "relative",
                display: "inline-block",
                marginBottom: 12,
              }}
            >
              <ArcGauge value={pct} size={120} stroke={11} color={scoreColor} />
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span
                  style={{
                    fontSize: 28,
                    fontWeight: 800,
                    color: "#0f172a",
                    lineHeight: 1,
                  }}
                >
                  {pct}%
                </span>
                <span
                  style={{ fontSize: 10, fontWeight: 600, color: "#1e293b" }}
                >
                  complete
                </span>
              </div>
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: scoreColor }}>
              {scoreLabel}
            </div>
          </div>

          <div
            style={{
              borderRadius: 20,
              padding: 20,
              background: "#ffffff",
              border: "1px solid rgba(99,102,241,0.12)",
              marginBottom: 16,
            }}
          >
            <h3
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#334155",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                marginBottom: 14,
              }}
            >
              Section Completion
            </h3>
            {SECTIONS.map((s) => {
              const filled = isSectionComplete(s.id, data);
              const SIcon = s.Icon;
              return (
                <div
                  key={s.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 8,
                  }}
                >
                  <SIcon size={11} color={s.accent} style={{ flexShrink: 0 }} />
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: "#0f172a",
                      width: 170,
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {s.title}
                  </span>
                  <div
                    style={{
                      flex: 1,
                      height: 5,
                      borderRadius: 9,
                      background: "#f1f5f9",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: filled ? "100%" : "0%",
                        height: "100%",
                        background: filled ? s.accent : "#e2e8f0",
                        borderRadius: 9,
                        transition: "width 1s cubic-bezier(0.4,0,0.2,1)",
                      }}
                    />
                  </div>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: filled ? s.accent : "#334155",
                      width: 40,
                      textAlign: "right",
                      flexShrink: 0,
                    }}
                  >
                    {filled ? "Done" : "–"}
                  </span>
                </div>
              );
            })}
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <button
              onClick={onReset}
              disabled={exporting}
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                padding: 14,
                borderRadius: 14,
                background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
                color: "#fff",
                fontSize: 13,
                fontWeight: 700,
                border: "none",
                cursor: "pointer",
                boxShadow: "0 6px 24px rgba(99,102,241,0.38)",
                opacity: exporting ? 0.6 : 1,
              }}
            >
              <RefreshCw size={14} /> Start New ROPA
            </button>
            <button
              onClick={handleExport}
              disabled={exporting}
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                padding: 14,
                borderRadius: 14,
                background: "#ffffff",
                color: "#1e293b",
                fontSize: 13,
                fontWeight: 600,
                border: "1px solid rgba(99,102,241,0.2)",
                cursor: exporting ? "wait" : "pointer",
                opacity: exporting ? 0.6 : 1,
              }}
            >
              <Download size={14} />{" "}
              {exporting ? "Exporting…" : "Export Record"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RopaFL({ children, required, fieldId, subsection }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        marginBottom: 6,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 4,
        }}
      >
        <label
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "#1e293b",
            textTransform: "uppercase",
            letterSpacing: "0.07em",
          }}
        >
          {children}
          {required && (
            <span style={{ color: "#ef4444", marginLeft: 3 }}>*</span>
          )}
        </label>
      </div>
      {(fieldId || subsection) && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginTop: 2,
            marginBottom: 4,
            flexWrap: "wrap",
          }}
        >
          {fieldId && (
            <span
              style={{
                fontSize: 9,
                fontFamily: "monospace",
                fontWeight: 600,
                padding: "2px 6px",
                borderRadius: 5,
                background: "#f1f5f9",
                color: "#1e293b",
                border: "1px solid rgba(99,102,241,0.1)",
              }}
            >
              {fieldId}
            </span>
          )}
          {subsection && (
            <span style={{ fontSize: 10, color: "#1e293b" }}>{subsection}</span>
          )}
        </div>
      )}
    </div>
  );
}

function RopaSchemaExtraFields({
  schemaFields = [],
  sectionId,
  d,
  set,
  errors = {},
}) {
  const HARDCODED = {
    org: [
      "name",
      "role",
      "address",
      "email",
      "phone",
      "grievance",
      "sdf",
      "lastUpdated",
    ],
    activity: ["name", "dept", "purpose", "desc"],
    legal: ["consent", "employment", "legalObligation", "publicInterest"],
    principals: ["customers", "employees", "applicants", "vendors", "minors"],
    personaldata: ["identity", "contact", "financial", "auth", "usageDevice"],
    lifecycle: ["source", "storage", "method", "access", "sharing"],
    sharing: [],
    retention: [],
    security: ["encryption", "accessCtrl", "logging", "incident", "backup"],
    rights: [],
    incident: [
      "identification",
      "dpbNotification",
      "timeline",
      "communication",
    ],
    approval: [],
  };
  const knownIds = new Set(HARDCODED[sectionId] || []);
  const extras = schemaFields
    .filter(
      (f) =>
        f.section_id === sectionId &&
        !knownIds.has(f.external_id) &&
        f.is_active !== false,
    )
    .sort((a, b) => (a.order || 0) - (b.order || 0));
  if (!extras.length) return null;
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 14,
        marginTop: 8,
      }}
    >
      {extras.map((f) => (
        <DynamicField
          key={f.external_id}
          field={f}
          d={d}
          set={set}
          errors={errors}
        />
      ))}
    </div>
  );
}

// ─── ROPA cover / choice page ──────────────────────────────────────────────────
// Landing page shown under Nav → Assessment → ROPA. Lets the user pick between
// filling the manual, guided ROPA form or (in future) the automated generator.
export function RopaCoverPage() {
  const navigate = useNavigate();

  const infoBlocks = [
    {
      icon: FileText,
      num: "01",
      title: "What is ROPA?",
      accent: "#2563EB",
      accentBg: "#EFF6FF",
      body: "A Record of Processing Activities (ROPA) is your organisation's master register of every personal data processing activity — capturing the purpose of processing, categories of data principals and personal data involved, the legal basis relied on, data flows to third parties or across borders, retention timelines, and the security safeguards applied. OneDPDP's guided form walks you through all of this across 12 structured sections, so nothing gets missed.",
    },
    {
      icon: Scale,
      num: "02",
      title: "Why is it required?",
      accent: "#7C3AED",
      accentBg: "#F5F3FF",
      body: "Section 8 of India's Digital Personal Data Protection Act, 2023 places the burden of accountability on every Data Fiduciary — you must be able to prove, not just claim, that personal data is processed lawfully. A current, well-maintained ROPA is the primary evidence the Data Protection Board and external auditors will ask for during an inquiry, and gaps here are among the most common compliance findings, often triggering penalties or corrective directions.",
    },
    {
      icon: CheckCircle2,
      num: "03",
      title: "How is it used?",
      accent: "#0D9488",
      accentBg: "#F0FDFA",
      body: "Once complete, your ROPA becomes the single source of truth that powers the rest of your privacy programme — feeding directly into Privacy Impact Assessments, breach notification timelines, DSAR fulfilment, vendor (TPRM) risk reviews, and your compliance roadmap score. Treat it as a living document and revisit it whenever a new activity, system, vendor, or data category is introduced.",
    },
  ];

  return (
    <div
      className="min-w-0 w-full"
      style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}
    >
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300..700;1,9..40,300..700&display=swap');`}</style>
      <div className="w-full min-w-0 px-3 sm:px-5 lg:px-6 py-4 sm:py-5">
        {/* ── Masthead ─────────────────────────────────────────────────── */}
        <div
          className="relative overflow-hidden rounded-2xl mb-4 shadow-lg"
          style={{
            background:
              "linear-gradient(120deg, #0b1024 0%, #1e1b4b 50%, #3730a3 100%)",
          }}
        >
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)",
              backgroundSize: "30px 30px",
              maskImage:
                "radial-gradient(ellipse 90% 100% at 15% 0%, black 15%, transparent 75%)",
              WebkitMaskImage:
                "radial-gradient(ellipse 90% 100% at 15% 0%, black 15%, transparent 75%)",
            }}
          />
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              top: -70,
              right: -50,
              width: 240,
              height: 240,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(129,140,248,0.45), transparent 70%)",
              filter: "blur(6px)",
            }}
          />
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              bottom: -90,
              left: "35%",
              width: 220,
              height: 220,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(45,212,191,0.22), transparent 70%)",
              filter: "blur(8px)",
            }}
          />

          <div
            className="relative flex flex-wrap items-center justify-between gap-3 px-5 sm:px-7 py-4 sm:py-5"
            data-tour-id="tour-ropa-cover-header"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div
                className="hidden sm:flex w-11 h-11 rounded-xl items-center justify-center shadow-md flex-shrink-0"
                style={{
                  background: "rgba(255,255,255,0.1)",
                  border: "1px solid rgba(255,255,255,0.18)",
                }}
              >
                <ClipboardList size={20} className="text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-xl font-bold text-white tracking-tight">
                  Record of Processing Activities
                </h1>
                <p className="text-[13px] text-indigo-100/75 mt-0.5 max-w-lg leading-snug">
                  Choose how you'd like to document your organisation's
                  processing activities.
                </p>
              </div>
            </div>
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide flex-shrink-0"
              style={{
                background: "rgba(255,255,255,0.12)",
                color: "#c7d2fe",
                border: "1px solid rgba(255,255,255,0.18)",
              }}
            >
              <Shield size={11} /> DPDP Act 2023 · Section 8
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 min-w-0">
          {/* ── Left: ledger-style info panel ───────────────────────────── */}
          <div className="lg:col-span-7 relative bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden min-w-0">
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                inset: 0,
                backgroundImage:
                  "repeating-linear-gradient(180deg, transparent, transparent 43px, rgba(148,163,184,0.09) 44px)",
              }}
            />
            <div className="relative divide-y divide-slate-100">
              {infoBlocks.map((block) => {
                const Icon = block.icon;
                return (
                  <div
                    key={block.title}
                    data-tour-id={
                      block.num === "01"
                        ? "tour-ropa-cover-what"
                        : block.num === "02"
                          ? "tour-ropa-cover-why"
                          : "tour-ropa-cover-how"
                    }
                    className="flex items-start gap-3.5 px-4 sm:px-6 py-4"
                    style={{ borderLeft: `3px solid ${block.accent}` }}
                  >
                    <span
                      className="font-mono text-[11px] font-bold pt-1 flex-shrink-0"
                      style={{ color: block.accent }}
                    >
                      {block.num}
                    </span>
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: block.accentBg, color: block.accent }}
                    >
                      <Icon size={16} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-[13px] font-bold text-slate-900 mb-0.5">
                        {block.title}
                      </h3>
                      <p className="text-[13px] text-slate-600 leading-relaxed">
                        {block.body}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Right: manual + automated choice cards ─────────────────── */}
          <div className="lg:col-span-5 flex flex-col gap-4 min-w-0">
            {/* Manual ROPA */}
            <div
              data-tour-id="tour-ropa-cover-manual"
              className="relative rounded-2xl p-[1.5px] shadow-sm min-w-0"
              style={{
                background:
                  "linear-gradient(135deg, #818cf8 0%, #6366f1 50%, #4338ca 100%)",
              }}
            >
              <div className="relative bg-white rounded-[15px] p-5 overflow-hidden">
                <div
                  aria-hidden="true"
                  className="absolute top-0 right-0 w-24 h-24 overflow-hidden pointer-events-none"
                >
                  <div
                    className="absolute top-[16px] right-[-32px] rotate-45 w-[130px] text-center text-[9px] font-bold uppercase tracking-wide text-white py-[3px]"
                    style={{
                      background: "linear-gradient(135deg,#6366f1,#4338ca)",
                      boxShadow: "0 2px 6px rgba(67,56,202,0.4)",
                    }}
                  >
                    Recommended
                  </div>
                </div>
                <div className="flex items-center gap-3 mb-2.5 pr-16">
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{
                      background:
                        "linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)",
                    }}
                  >
                    <PenLine size={16} className="text-white" />
                  </div>
                  <h2 className="text-[15px] font-bold text-slate-900">
                    Manual ROPA
                  </h2>
                </div>
                <p className="text-[13px] text-slate-600 leading-relaxed mb-3">
                  Fill out a guided, section-by-section form covering your
                  organisation, processing activities, legal basis, data
                  flows, retention, and security controls.
                </p>
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-100">
                    <LayoutList size={11} /> 12 guided sections
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold text-slate-600 bg-slate-100 border border-slate-200">
                    <Clock size={11} /> ~20–25 min
                  </span>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch gap-2.5 min-w-0">
                  <button
                    data-tour-id="tour-ropa-cover-start"
                    onClick={() => navigate("/user/ropa/manual")}
                    className="group flex-1 min-w-0 relative inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-semibold text-white transition-all duration-300 hover:shadow-xl active:scale-[0.98]"
                    style={{
                      background:
                        "linear-gradient(135deg, #6366f1 0%, #4f46e5 45%, #4338ca 100%)",
                      boxShadow:
                        "0 8px 20px -4px rgba(79,70,229,0.45), inset 0 1px 0 rgba(255,255,255,0.2)",
                    }}
                  >
                    <span className="truncate">Start Manual ROPA</span>
                    <ArrowRight
                      size={14}
                      className="flex-shrink-0 transition-transform duration-300 group-hover:translate-x-0.5"
                    />
                  </button>

                  <button
                    data-tour-id="tour-ropa-view-submissions"
                    onClick={() => navigate("/user/ropa/submissions")}
                    className="group flex-1 min-w-0 relative inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-semibold overflow-hidden transition-all duration-300 hover:shadow-[0_8px_20px_-6px_rgba(99,102,241,0.4)] active:scale-[0.98]"
                    style={{
                      background: "#ffffff",
                      color: "#4f46e5",
                      border: "1.5px solid #e0e4fb",
                    }}
                  >
                    <span
                      aria-hidden="true"
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      style={{
                        background:
                          "linear-gradient(135deg, #eef1ff 0%, #e4e8ff 100%)",
                      }}
                    />
                    <ClipboardList
                      size={14}
                      className="relative flex-shrink-0"
                    />
                    <span className="relative truncate">View Submissions</span>
                    <ArrowRight
                      size={14}
                      className="relative flex-shrink-0 transition-transform duration-300 group-hover:translate-x-0.5"
                    />
                  </button>
                </div>
              </div>
            </div>

            {/* Automated ROPA */}
            <div
              data-tour-id="tour-ropa-cover-automated"
              className="relative bg-slate-50/60 rounded-2xl border border-dashed border-indigo-200 shadow-sm p-5 overflow-hidden min-w-0"
            >
              <div
                aria-hidden="true"
                className="absolute top-0 right-0 w-24 h-24 overflow-hidden pointer-events-none"
              >
                <div
                  className="absolute top-[16px] right-[-32px] rotate-45 w-[130px] text-center text-[9px] font-bold uppercase tracking-wide text-white py-[3px]"
                  style={{
                    background: "linear-gradient(135deg,#8b5cf6,#6366f1)",
                    boxShadow: "0 2px 6px rgba(99,102,241,0.4)",
                  }}
                >
                  Auto-Synced
                </div>
              </div>
              <div className="flex items-center gap-3 mb-2.5 pr-16">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{
                    background:
                      "linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)",
                  }}
                >
                  <Bot size={16} className="text-white" />
                </div>
                <h2 className="text-[15px] font-bold text-slate-900">
                  Automated ROPA
                </h2>
              </div>
              <p className="text-[13px] text-slate-600 leading-relaxed mb-3">
                Records auto-pushed into OneDPDP from connected DSPM systems
                like DataForesight — reviewed, scored, and kept in sync
                automatically.
              </p>
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold text-purple-700 bg-purple-50 border border-purple-100">
                  <RefreshCw size={11} /> Auto-synced
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold text-slate-600 bg-slate-100 border border-slate-200">
                  <Database size={11} /> Requires DSPM connection
                </span>
              </div>
              <button
                data-tour-id="tour-ropa-cover-automated-btn"
                onClick={() => navigate("/user/ropa/automated")}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-[13px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 transition-colors hover:bg-indigo-100 w-full sm:w-auto"
              >
                View Automated ROPA
                <ArrowRight size={14} className="flex-shrink-0" />
              </button>
            </div>
          </div>
        </div>

        {/* ── Trust strip ──────────────────────────────────────────────── */}
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-[11px] text-slate-400 text-center px-2">
          <Lock size={11} className="flex-shrink-0" />
          <span>
            Your responses are encrypted in transit and visible only within
            your organisation
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function ROPAForm() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [data, setData] = useState(() => initData());
  const [submitted, setSubmitted] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [errors, setErrors] = useState({});
  const [departmentId, setDepartmentId] = useState("");
  const [loadingDept, setLoadingDept] = useState(false);
  const [schemaFields, setSchemaFields] = useState([]); // ← add
  const [isLocked, setIsLocked] = useState(false);
  const [lockInfo, setLockInfo] = useState(null);
  const { departments, loading: departmentsLoading } = useDepartments();
  useAutoSelectAssignedDepartment({
    departmentId,
    setDepartmentId,
    departments,
    loading: departmentsLoading,
  });
  const liveTour = useLiveTourOptional();
  const tourPreviewDepartmentId = getLiveTourPreviewDepartmentId(
    liveTour,
    departments,
    departmentsLoading,
  );
  const activeDepartmentId = departmentId || tourPreviewDepartmentId;
  useLiveTourDepartmentPrep(
    departmentId,
    setDepartmentId,
    departments,
    departmentsLoading,
  );
  const saveTimer = useRef(null);
  const mainRef = useRef();

  // Fetch tenant schema on mount
  useEffect(() => {
    const loadSchema = () => {
      apiFetch("/ropa/schema")
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (d?.fields?.length) {
            const normalized = normalizeRopaSchemaFields(d.fields);
            setSchemaFields(normalized);
            window.__ropaSchemaFields = normalized;
          }
        })
        .catch(() => {});
    };
    loadSchema();
    const onFocus = () => loadSchema();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  const buildPayload = useCallback(
    () => ({ step, form_data: data, department_id: departmentId || null }),
    [step, data, departmentId],
  );

  const handleDepartmentChange = (e) => {
    clearTimeout(saveTimer.current);
    setDraftLoaded(false);
    setDepartmentId(e.target.value);
  };

  const loadDepartmentAssessment = useCallback(async (deptId) => {
    if (!deptId) {
      setData(initData());
      setStep(0);
      setIsLocked(false);
      setLockInfo(null);
      setDraftLoaded(true);
      return;
    }
    setLoadingDept(true);
    setDraftLoaded(false);
    try {
      const q = encodeURIComponent(deptId);
      const res = await apiFetch(`/ropa/draft?department_id=${q}`);
      if (res.ok) {
        const draft = await res.json();
        const locked = detectLocked(draft);
        setIsLocked(locked);
        setLockInfo(
          locked
            ? {
                submittedAt: draft.submitted_at || draft.updated_at || null,
                submittedBy:
                  draft.submitted_by_name || draft.submitted_by || null,
              }
            : null,
        );
        if (draft.form_data && Object.keys(draft.form_data).length) {
          setData(mergeRopaData(draft.form_data));
          if (typeof draft.step === "number") setStep(draft.step);
        } else {
          setData(initData());
          setStep(0);
        }
      } else {
        setData(initData());
        setStep(0);
        setIsLocked(false);
        setLockInfo(null);
      }
      setSubmitError("");
    } catch {
      setData(initData());
      setStep(0);
      setIsLocked(false);
      setLockInfo(null);
    } finally {
      setLoadingDept(false);
      setDraftLoaded(true);
    }
  }, []);

  useEffect(() => {
    loadDepartmentAssessment(activeDepartmentId);
  }, [activeDepartmentId, loadDepartmentAssessment]);

  useEffect(() => {
    if (!draftLoaded || submitted || isLocked || !departmentId) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        await apiFetch("/ropa/draft", {
          method: "PUT",
          body: JSON.stringify(buildPayload()),
        });
      } catch {
        // silent
      } finally {
        setSaving(false);
      }
    }, 900);
    return () => clearTimeout(saveTimer.current);
  }, [draftLoaded, submitted, isLocked, departmentId, buildPayload]);

  const handleSubmit = async () => {
    if (isLocked) {
      setSubmitError(
        "This ROPA has already been submitted for this department and is locked for editing.",
      );
      return;
    }
    if (!departmentId) {
      setSubmitError("Please select your department before submitting.");
      return;
    }
    const invalid = findFirstInvalidStep(data, schemaFields);
    if (invalid) {
      const jump = invalid.stepIndex !== step;
      if (jump) goTo(invalid.stepIndex);
      setErrors(invalid.errors);
      afterValidationRender(() => {
        setTimeout(
          () =>
            scrollToFirstInvalidField(Object.keys(invalid.errors), {
              container: mainRef,
            }),
          jump ? 280 : 0,
        );
      });
      return;
    }
    setErrors({});
    setSubmitting(true);
    setSubmitError("");
    try {
      const res = await apiFetch("/ropa/submit", {
        method: "POST",
        body: JSON.stringify(buildPayload()),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Submission failed");
      }
      setSubmitted(true);
    } catch (e) {
      setSubmitError(e.message || "Could not submit ROPA");
    } finally {
      setSubmitting(false);
    }
  };

  const makeSet = (id) => (key) => (val) =>
    setData((prev) => ({ ...prev, [id]: { ...prev[id], [key]: val } }));

  const goTo = (i) => {
    if (i === step || transitioning) return;
    setTransitioning(true);
    setTimeout(() => {
      setStep(i);
      setTransitioning(false);
    }, 200);
    setMobileNav(false);
    if (mainRef.current)
      mainRef.current.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const onTourLastSection = () => {
      if (SECTIONS.length === 0) return;
      const last = SECTIONS.length - 1;
      setStep(last);
      setMobileNav(false);
      window.setTimeout(() => {
        mainRef.current?.scrollTo({ top: 0, behavior: "smooth" });
        notifyLiveTourTargetsChanged();
      }, 280);
    };
    window.addEventListener("live-tour:go-to-last-section", onTourLastSection);
    return () =>
      window.removeEventListener("live-tour:go-to-last-section", onTourLastSection);
  }, []);

  const handleNext = () => {
    if (isLocked) {
      if (step < SECTIONS.length - 1) goTo(step + 1);
      return;
    }
    const sectionId = SECTIONS[step].id;
    const errs = validateSection(sectionId, data, schemaFields);
    if (Object.keys(errs).length) {
      setErrors(errs);
      afterValidationRender(() => {
        scrollToFirstInvalidField(Object.keys(errs), { container: mainRef });
      });
      return;
    }
    setErrors({});
    goTo(step + 1);
  };

  const sec = SECTIONS[step];
  const SecIcon = sec.Icon;
  const sData = data[sec.id] || {};
  const set = makeSet(sec.id);
  const fieldCount = getSectionFilled(sec.id, data, schemaFields);
  const sectionComplete = isSectionComplete(sec.id, data, schemaFields);
  const totalFilled = SECTIONS.filter((s) =>
    isSectionComplete(s.id, data, schemaFields),
  ).length;

  if (submitted) {
    return (
      <>
        <style>{CSS}</style>
        <ResultScreen
          data={data}
          onReset={() => {
            setSubmitted(false);
            setData(initData());
            setStep(0);
          }}
        />
      </>
    );
  }

  const SidebarOuter = () => (
    <aside
      data-tour-id="tour-ropa-sidebar"
      style={{
        width: 260,
        flexShrink: 0,
        background: "#ffffff",
        borderRight: "1px solid rgba(99,102,241,0.14)",
        boxShadow: "2px 0 12px rgba(99,102,241,0.06)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
      className="ropa-desktop-sidebar"
    >
      <SidebarContent step={step} data={data} onGoTo={goTo} />
    </aside>
  );

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        height: "100%",
        overflow: "hidden",
        background: "#ffffff",
        fontFamily: "'DM Sans',system-ui,-apple-system,sans-serif",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <style>{CSS}</style>

      {/* Header */}
      <header
        data-tour-id="tour-ropa-header"
        style={{
          background: "#ffffff",
          boxShadow:
            "0 1px 0 0 rgba(99,102,241,0.18),0 2px 8px rgba(0,0,0,0.04)",
          borderBottom: "1px solid rgba(99,102,241,0.10)",
          padding: "10px 16px",
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexShrink: 0,
        }}
      >
        <button
          onClick={() => setMobileNav(true)}
          className="ropa-mob-btn"
          style={{
            padding: 6,
            borderRadius: 8,
            background: "#ffffff",
            border: "1px solid rgba(99,102,241,0.15)",
            color: "#334155",
            flexShrink: 0,
            cursor: "pointer",
            display: "none",
          }}
        >
          <Menu size={18} />
        </button>

        {/* Brand */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              background:
                "linear-gradient(135deg,#6366f1 0%,#8b5cf6 50%,#a855f7 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow:
                "0 4px 18px rgba(99,102,241,0.55),0 2px 8px rgba(139,92,246,0.35)",
            }}
          >
            <ShieldCheck size={16} color="#fff" />
          </div>
          <div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: "#0f172a",
                lineHeight: 1.2,
              }}
            >
              ROPA
            </div>
            <div style={{ fontSize: 10, color: "#334155", lineHeight: 1.2 }}>
              ROPA · DPDP Act 2023
            </div>
          </div>
        </div>

        {/* Centre — department selector */}
        <div
          data-tour-id="tour-ropa-department"
          style={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            minWidth: 0,
          }}
        >
          <DepartmentDropdown
            value={departmentId}
            onChange={handleDepartmentChange}
            departments={departments}
            loading={departmentsLoading}
            lockToAssigned
            style={{ flex: "1 1 200px", maxWidth: 320 }}
          />
          {isLocked && departmentId && (
            <span
              title="This department's ROPA has already been submitted and is read-only"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                marginLeft: 8,
                padding: "5px 10px",
                borderRadius: 999,
                background: "linear-gradient(135deg,#f8fafc,#eef2f7)",
                border: "1px solid #cbd5e1",
                color: "#475569",
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: 0.2,
                flexShrink: 0,
              }}
            >
              <Lock size={11} /> Locked
            </span>
          )}
        </div>

        {/* Stats + Submit */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexShrink: 0,
          }}
          data-tour-id="tour-ropa-stats"
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "6px 14px",
              borderRadius: 10,
              background: "#ffffff",
              border: "1px solid rgba(99,102,241,0.15)",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>
                {totalFilled}
              </div>
              <div style={{ fontSize: 10, fontWeight: 600, color: "#1e293b" }}>
                Filled
              </div>
            </div>
            <div
              style={{
                width: 1,
                height: 22,
                background: "rgba(99,102,241,0.2)",
              }}
            />
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>
                {step + 1}
                <span style={{ color: "#334155", fontWeight: 500 }}>
                  /{SECTIONS.length}
                </span>
              </div>
              <div style={{ fontSize: 10, fontWeight: 600, color: "#1e293b" }}>
                Section
              </div>
            </div>
          </div>
          {loadingDept && (
            <span style={{ fontSize: 10, color: "#64748b", fontWeight: 500 }}>
              Loading…
            </span>
          )}
          {saving && (
            <span style={{ fontSize: 10, color: "#64748b", fontWeight: 500 }}>
              Saving…
            </span>
          )}
          {submitError && (
            <span style={{ fontSize: 10, color: "#ef4444", fontWeight: 500 }}>
              {submitError}
            </span>
          )}
          <button
            data-tour-id="tour-ropa-view-table"
            onClick={() => navigate("/user/ropa/submissions")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "7px 14px",
              borderRadius: 10,
              background: "#ffffff",
              color: "#6366f1",
              fontSize: 11,
              fontWeight: 600,
              border: "1px solid rgba(99,102,241,0.25)",
              boxShadow: "0 2px 8px rgba(99,102,241,0.1)",
              cursor: "pointer",
            }}
          >
            <LayoutList size={13} /> View Table
          </button>
          {isLocked ? (
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "7px 16px",
                borderRadius: 10,
                background: "#f1f5f9",
                color: "#64748b",
                fontSize: 11,
                fontWeight: 600,
                border: "1px solid #cbd5e1",
              }}
            >
              <Lock size={13} /> Read-only
            </span>
          ) : (
            <button
              data-tour-id="tour-ropa-submit-btn"
              onClick={handleSubmit}
              disabled={submitting || !departmentId}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "7px 16px",
                borderRadius: 10,
                background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
                color: "#fff",
                fontSize: 11,
                fontWeight: 600,
                boxShadow: "0 4px 14px rgba(99,102,241,0.35)",
                border: "none",
                cursor: submitting || !departmentId ? "not-allowed" : "pointer",
                opacity: submitting || !departmentId ? 0.7 : 1,
              }}
            >
              <ShieldCheck size={13} />{" "}
              {submitting ? "Submitting…" : "Submit ROPA"}
            </button>
          )}
        </div>
      </header>

      {!departmentId && (
        <div
          style={{
            flexShrink: 0,
            padding: "8px 16px",
            background: "#fffbeb",
            borderBottom: "1px solid rgba(245,158,11,0.2)",
            fontSize: 11,
            color: "#92400e",
            textAlign: "center",
          }}
        >
          Select a department above — each department has its own ROPA draft and
          submission.
        </div>
      )}

      {isLocked && departmentId && (
        <div
          style={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            padding: "8px 16px",
            background: "#f8fafc",
            borderBottom: "1px solid #cbd5e1",
            fontSize: 11.5,
            color: "#475569",
            textAlign: "center",
          }}
        >
          <Lock size={12} />
          This ROPA has already been submitted for this department. You&rsquo;re
          viewing the submitted responses in read-only mode.
          {lockInfo?.submittedAt && (
            <span style={{ color: "#94a3b8" }}>
              &nbsp;· Submitted{" "}
              {new Date(lockInfo.submittedAt).toLocaleDateString()}
            </span>
          )}
        </div>
      )}

      <div
        style={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden" }}
      >
        {!activeDepartmentId ? (
          <DepartmentSelectionRequired message="Please select a department from the dropdown in the header to load, view, or fill the ROPA form." />
        ) : (
          <>
        {/* Mobile overlay */}
        {mobileNav && (
          <Overlay
            open={mobileNav}
            onClose={() => setMobileNav(false)}
            className="bg-slate-900/25 backdrop-blur-sm"
          >
            <div
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: 280,
                background: "#ffffff",
                borderRight: "1px solid rgba(99,102,241,0.15)",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "12px 16px",
                  borderBottom: "1px solid rgba(99,102,241,0.12)",
                  flexShrink: 0,
                }}
              >
                <span
                  style={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}
                >
                  Navigation
                </span>
                <button
                  onClick={() => setMobileNav(false)}
                  style={{
                    color: "#334155",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  <X size={16} />
                </button>
              </div>
              <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
                <SidebarContent step={step} data={data} onGoTo={goTo} />
              </div>
            </div>
          </Overlay>
        )}

        {/* Desktop sidebar */}
        <SidebarOuter />

        {/* Main content */}
        <main
          ref={mainRef}
          style={{
            flex: 1,
            overflowY: "auto",
            height: "100%",
            background: "#ffffff",
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(99,102,241,0.3) #e2e8f0",
            position: "relative",
          }}
        >
            <div
              style={{
                width: "100%",
                padding: "20px 24px 40px",
              }}
            >
              {/* Section banner — identical layout to GAP */}
              <div
                data-tour-id="tour-ropa-section-banner"
                style={{
                  borderRadius: 20,
                  padding: "18px 20px",
                  marginBottom: 18,
                  background: "#ffffff",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "flex-start", gap: 14 }}
                >
                  <div
                    style={{
                      width: 46,
                      height: 46,
                      borderRadius: 14,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      background: `linear-gradient(135deg,${sec.accent}ee 0%,${sec.accent}88 100%)`,
                      border: `1px solid ${sec.accent}40`,
                      boxShadow: `0 4px 16px ${sec.accent}40`,
                    }}
                  >
                    <SecIcon size={20} color="#fff" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flexWrap: "wrap",
                      }}
                    >
                      <h2
                        style={{
                          fontSize: 16,
                          fontWeight: 700,
                          color: "#0f172a",
                        }}
                      >
                        {sec.title}
                      </h2>
                      <span
                        style={{
                          fontSize: 10,
                          fontFamily: "monospace",
                          fontWeight: 600,
                          padding: "2px 8px",
                          borderRadius: 6,
                          background: sec.accent + "1a",
                          color: sec.accent,
                          border: `1px solid ${sec.accent}28`,
                        }}
                      >
                        {sec.num}
                      </span>
                      {sectionComplete && (
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 600,
                            padding: "2px 8px",
                            borderRadius: 6,
                            background: "#ecfdf5",
                            color: "#059669",
                            border: "1px solid #a7f3d0",
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <Check size={9} /> Complete
                        </span>
                      )}
                      {isLocked && (
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            padding: "2px 8px",
                            borderRadius: 6,
                            background: "#f1f5f9",
                            color: "#64748b",
                            border: "1px solid #cbd5e1",
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <Lock size={9} /> View Only
                        </span>
                      )}
                      {!sectionComplete && fieldCount > 0 && (
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 600,
                            padding: "2px 8px",
                            borderRadius: 6,
                            background: sec.accent + "12",
                            color: sec.accent,
                            border: `1px solid ${sec.accent}28`,
                          }}
                        >
                          {fieldCount} field{fieldCount !== 1 ? "s" : ""}{" "}
                          started
                        </span>
                      )}
                    </div>
                    <p
                      style={{
                        fontSize: 12,
                        color: "#334155",
                        marginTop: 3,
                        lineHeight: 1.5,
                      }}
                    >
                      {SECTION_DESCS[sec.id]}
                    </p>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 16,
                        marginTop: 10,
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        style={{
                          fontSize: 11,
                          color: "#334155",
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                        }}
                      >
                        <FileText size={11} /> Section {step + 1} of{" "}
                        {SECTIONS.length}
                      </span>
                      <div
                        style={{
                          marginLeft: "auto",
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: sec.accent,
                          }}
                        >
                          {sectionComplete
                            ? "Done"
                            : fieldCount > 0
                              ? "In Progress"
                              : "Not started"}
                        </span>
                        <div
                          style={{
                            width: 80,
                            height: 5,
                            borderRadius: 9,
                            background: "#f1f5f9",
                            overflow: "hidden",
                          }}
                        >
                          <div
                            style={{
                              width: sectionComplete ? "100%" : "0%",
                              height: "100%",
                              background: sec.accent,
                              borderRadius: 9,
                              transition: "width 0.7s ease",
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Form content */}
              <div
                data-tour-id="tour-ropa-form"
                style={{
                  position: "relative",
                  opacity: transitioning ? 0 : 1,
                  transform: transitioning ? "translateY(8px)" : "none",
                  transition: "opacity 0.22s ease, transform 0.22s ease",
                }}
              >
                {isLocked && (
                  <div
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      inset: 0,
                      zIndex: 5,
                      cursor: "not-allowed",
                      background:
                        "repeating-linear-gradient(135deg, rgba(148,163,184,0.05) 0px, rgba(148,163,184,0.05) 14px, rgba(148,163,184,0.09) 14px, rgba(148,163,184,0.09) 28px)",
                      borderRadius: 16,
                    }}
                  />
                )}
                <div
                  style={{
                    pointerEvents: isLocked ? "none" : "auto",
                    opacity: isLocked ? 0.72 : 1,
                    filter: isLocked
                      ? "grayscale(0.25) saturate(0.85)"
                      : "none",
                    userSelect: isLocked ? "text" : "auto",
                    transition: "opacity 0.2s ease, filter 0.2s ease",
                  }}
                >
                  {sec.id === "org" && (
                    <OrgPanel
                      d={sData}
                      set={set}
                      errors={errors}
                      schemaFields={schemaFields}
                    />
                  )}
                  {sec.id === "activity" && (
                    <ActivityPanel
                      d={sData}
                      set={set}
                      errors={errors}
                      schemaFields={schemaFields}
                    />
                  )}
                  {sec.id === "legal" && (
                    <LegalPanel
                      d={sData}
                      set={set}
                      accent={sec.accent}
                      errors={errors}
                      schemaFields={schemaFields}
                    />
                  )}
                  {sec.id === "principals" && (
                    <PrincipalsPanel
                      d={sData}
                      set={set}
                      accent={sec.accent}
                      errors={errors}
                      schemaFields={schemaFields}
                    />
                  )}
                  {sec.id === "personaldata" && (
                    <PersonalDataPanel
                      d={sData}
                      set={set}
                      accent={sec.accent}
                      errors={errors}
                      schemaFields={schemaFields}
                    />
                  )}
                  {sec.id === "lifecycle" && (
                    <LifecyclePanel
                      d={sData}
                      set={set}
                      errors={errors}
                      schemaFields={schemaFields}
                    />
                  )}
                  {sec.id === "sharing" && (
                    <SharingPanel
                      d={sData}
                      set={set}
                      accent={sec.accent}
                      errors={errors}
                      schemaFields={schemaFields}
                    />
                  )}
                  {sec.id === "retention" && (
                    <RetentionPanel
                      d={sData}
                      set={set}
                      accent={sec.accent}
                      errors={errors}
                      schemaFields={schemaFields}
                    />
                  )}
                  {sec.id === "security" && (
                    <SecurityPanel
                      d={sData}
                      set={set}
                      accent={sec.accent}
                      errors={errors}
                      schemaFields={schemaFields}
                    />
                  )}
                  {sec.id === "rights" && (
                    <RightsPanel
                      d={sData}
                      set={set}
                      accent={sec.accent}
                      errors={errors}
                      schemaFields={schemaFields}
                    />
                  )}
                  {sec.id === "incident" && (
                    <IncidentPanel
                      d={sData}
                      set={set}
                      errors={errors}
                      schemaFields={schemaFields}
                    />
                  )}
                  {sec.id === "approval" && (
                    <ApprovalPanel
                      d={sData}
                      set={set}
                      errors={errors}
                      schemaFields={schemaFields}
                    />
                  )}
                  <RopaSchemaExtraFields
                    schemaFields={schemaFields}
                    sectionId={sec.id}
                    d={sData}
                    set={set}
                    errors={errors}
                  />
                </div>
                {isLocked && (
                  <div
                    style={{
                      position: "sticky",
                      bottom: 12,
                      display: "flex",
                      justifyContent: "center",
                      marginTop: 14,
                      pointerEvents: "none",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 7,
                        padding: "7px 16px",
                        borderRadius: 999,
                        background: "rgba(15,23,42,0.85)",
                        color: "#f8fafc",
                        fontSize: 11,
                        fontWeight: 600,
                        boxShadow: "0 6px 20px rgba(15,23,42,0.25)",
                        backdropFilter: "blur(4px)",
                      }}
                    >
                      <Lock size={12} /> Submitted — read only
                    </div>
                  </div>
                )}
              </div>

              {/* Footer nav — identical to GAP */}
              <div
                data-tour-id="tour-ropa-nav-footer"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginTop: 24,
                  gap: 12,
                }}
              >
                <button
                  onClick={() => goTo(Math.max(0, step - 1))}
                  disabled={step === 0}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    padding: "9px 16px",
                    borderRadius: 12,
                    background: "#ffffff",
                    border: "1px solid rgba(99,102,241,0.18)",
                    color: step === 0 ? "#475569" : "#334155",
                    fontSize: 12,
                    fontWeight: 500,
                    cursor: step === 0 ? "not-allowed" : "pointer",
                    opacity: step === 0 ? 0.4 : 1,
                    transition: "all 0.15s",
                  }}
                >
                  <ChevronLeft size={15} /> Previous
                </button>

                {/* Dot stepper */}
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  {SECTIONS.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => goTo(i)}
                      style={{
                        height: 6,
                        borderRadius: 9,
                        border: "none",
                        cursor: "pointer",
                        width: i === step ? 22 : 6,
                        background:
                          i === step
                            ? sec.accent
                            : i < step
                              ? sec.accent + "55"
                              : "rgba(99,102,241,0.2)",
                        transition: "all 0.25s ease",
                      }}
                    />
                  ))}
                </div>

                {isLocked ? (
                  step < SECTIONS.length - 1 ? (
                    <button
                      onClick={() => goTo(step + 1)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 7,
                        padding: "9px 18px",
                        borderRadius: 12,
                        background: "#ffffff",
                        color: "#334155",
                        fontSize: 12,
                        fontWeight: 600,
                        border: "1px solid rgba(99,102,241,0.18)",
                        cursor: "pointer",
                        transition: "all 0.15s",
                      }}
                    >
                      Next <ChevronRight size={15} />
                    </button>
                  ) : (
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 7,
                        padding: "9px 20px",
                        borderRadius: 12,
                        background: "#f1f5f9",
                        color: "#64748b",
                        fontSize: 12,
                        fontWeight: 700,
                        border: "1px solid #cbd5e1",
                      }}
                    >
                      <Lock size={14} /> Locked — Already Submitted
                    </span>
                  )
                ) : step < SECTIONS.length - 1 ? (
                  <button
                    onClick={handleNext}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 7,
                      padding: "9px 18px",
                      borderRadius: 12,
                      background: `linear-gradient(135deg,${sec.accent},${sec.accent}bb)`,
                      color: "#fff",
                      fontSize: 12,
                      fontWeight: 600,
                      border: "none",
                      cursor: "pointer",
                      boxShadow: `0 4px 16px ${sec.accent}40`,
                      transition: "all 0.15s",
                    }}
                  >
                    Save & Next <ChevronRight size={15} />
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 7,
                      padding: "9px 20px",
                      borderRadius: 12,
                      background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
                      color: "#fff",
                      fontSize: 12,
                      fontWeight: 700,
                      border: "none",
                      cursor: submitting ? "not-allowed" : "pointer",
                      opacity: submitting ? 0.7 : 1,
                      boxShadow: "0 4px 20px rgba(99,102,241,0.45)",
                      transition: "all 0.15s",
                    }}
                  >
                    <ShieldCheck size={14} />{" "}
                    {submitting ? "Submitting…" : "Submit ROPA"}
                  </button>
                )}
              </div>
            </div>
        </main>
          </>
        )}
      </div>
    </div>
  );
}

// ─── CSS ──────────────────────────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300..700;1,9..40,300..700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #f8fafc; }
  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: #e2e8f0; border-radius: 9px; }
  ::-webkit-scrollbar-thumb { background: linear-gradient(180deg, rgba(99,102,241,0.55), rgba(139,92,246,0.45)); border-radius: 9px; }
  ::-webkit-scrollbar-thumb:hover { background: linear-gradient(180deg, rgba(99,102,241,0.75), rgba(139,92,246,0.65)); }
  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: #e2e8f0; border-radius: 9px; }
  ::-webkit-scrollbar-thumb { background: linear-gradient(180deg, rgba(99,102,241,0.55), rgba(139,92,246,0.45)); border-radius: 9px; }
  ::-webkit-scrollbar-thumb:hover { background: linear-gradient(180deg, rgba(99,102,241,0.75), rgba(139,92,246,0.65)); }
  input, textarea, select, button { font-family: inherit; }
  textarea { resize: vertical; }
  select option { background: #ffffff; color: #0f172a; }
  input[type=date]::-webkit-calendar-picker-indicator { opacity: 0.45; cursor: pointer; filter: saturate(0); }
  @media (max-width: 900px) { .ropa-mob-btn { display: flex !important; } .ropa-desktop-sidebar { display: none !important; } }
`;
